import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Home,
  AlertTriangle,
  Download,
  Filter,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { portfolioAPI, propertyCostsAPI } from '../lib/api';
import { formatCurrency, shortPropertyName } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { computeDerivedMetrics, convertDerivedToDisplayCurrency, buildBookingsTabMonthlyGroups } from '../lib/portfolioDerived';
import { formatCompact, formatPercent, currencySymbol, REGION_COLORS, PRIOR_YEAR_COLOR } from '../lib/chartFormatters';
import { PropertyMonthHeatmap } from '../components/PropertyMonthHeatmap';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { PayoutStateBadge } from '../components/PayoutStateBadge';
import { usePortfolioPrefsStore } from '../store';
import { useForecast } from '../hooks/useForecast';
import { ForecastTotalsFooter } from '../components/dashboard/ForecastTotalsFooter';
import {
  mergeWithForecast,
  splitForecastSeries,
  forecastLabelContent,
  ForecastCaveat,
  ForecastBarShape,
  getActualsForecastBoundary,
} from '../lib/forecastChartUtils';
import { COST_BUCKET_LABELS, COST_BUCKET_LABELS_SHORT, PNL_SUBTOTAL_LABELS, PNL_SUBTOTAL_LABELS_SHORT } from '../lib/costTaxonomy';

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CHANNEL_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];


// ─── Chart helpers ───────────────────────────────────────────────────────────
// fmtK and formatCurrencyShort replaced by shared formatCompact from chartFormatters.js

// ─── KPI Card ────────────────────────────────────────────────────────────────

function KpiCard({ label, value, subDetail, tooltip }) {
  return (
    <Card>
      <CardHeader className="pb-1 px-4 pt-4">
        <CardTitle className="text-xs font-medium text-muted-foreground truncate" title={tooltip || label}>{label}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="text-xl font-bold font-mono leading-tight">{value}</div>
        {subDetail && (
          <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{subDetail}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Shared KPI Cards ────────────────────────────────────────────────────────

/**
 * Parameterized KPI card set rendered once per scope: 'combined', 'uk', 'fr'.
 *
 * Combined scope: two rows of cards, each with a UK/FR native-currency sub-line.
 * Region scope: flat row without sub-lines (single-region, no split needed).
 *
 * @param {string} scope - 'combined' | 'uk' | 'fr'
 * @param {object} derived - full derived metrics object
 * @param {string} displayCurrency - 'GBP' | 'EUR'
 */
function PortfolioKpiCards({ scope, derived, displayCurrency, nativeTotals }) {
  const dc = displayCurrency;
  const t = derived.totals;

  // Resolve the right totals object and field accessors per scope.
  // Combined totals use _gbp suffixed keys; region totals use direct keys.
  const isCombined = scope === 'combined';
  const regionTotals = isCombined ? null : t[scope];
  const combinedTotals = t.combined;

  const grossRevenue = isCombined ? combinedTotals.gross_revenue_gbp : regionTotals.gross_revenue;
  // Net Revenue (A148): after OTA, management fees, cleaning and repairs.
  const netRevenue = isCombined ? combinedTotals.net_revenue_gbp : regionTotals.net_revenue;
  const netResult = isCombined ? combinedTotals.net_margin_gbp : regionTotals.net_margin;
  const grossMargin = isCombined ? combinedTotals.gross_margin_gbp : regionTotals.gross_margin;
  const occupancy = isCombined ? combinedTotals.occupancy : regionTotals.occupancy;
  const adr = isCombined ? combinedTotals.avg_nightly_rate_gbp : regionTotals.adr;
  const revpar = isCombined ? (combinedTotals.revpar_gbp ?? 0) : (regionTotals.revpar ?? 0);
  const propertiesCount = isCombined ? combinedTotals.properties_count : regionTotals.properties_count;

  // One basis on every Property Portfolio page (Fan, 2026-10-25): bookings
  // checking in from January to the end of THIS month, minus every cost of
  // those months. Same figures as the P&L, Revenue, Costs and Reporting
  // pages (backend actuals_cutoff_date = first of next month). Later months
  // are the forecast. The old actuals-to-today split left the cards about
  // 25k below the P&L and below their own chart footers.
  const grossRevenueActual = grossRevenue;
  const netRevenueActual = netRevenue;
  const netResultActual = netResult;
  const grossMarginActual = grossMargin;
  const grossMarginPctActual = grossRevenueActual > 0 ? (grossMarginActual / grossRevenueActual) * 100 : 0;
  const netMarginPctActual = grossRevenueActual > 0 ? (netResultActual / grossRevenueActual) * 100 : 0;

  // Per-region NATIVE-currency values for the UK/FR sub-lines (combined
  // scope only). `derived` is already converted to the display currency, so
  // its FR totals were GBP while the sub-line printed a euro sign
  // (EUR 341,136 shown was really GBP). Read the unconverted base.
  const nt = nativeTotals || t;
  const uk = isCombined ? nt.uk : null;
  const fr = isCombined ? nt.fr : null;

  // Helper: build "\u{1F1EC}\u{1F1E7} {ukVal} / \u{1F1EB}\u{1F1F7} {frVal}" sub-line for combined scope.
  const regionSub = (ukVal, frVal) =>
    `\u{1F1EC}\u{1F1E7} ${ukVal} / \u{1F1EB}\u{1F1F7} ${frVal}`;

  return (
    <div className="space-y-3">
      {/* Row 1: Gross Revenue, Net Revenue, Gross Margin, Net Margin */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Booking Volume"
          value={formatCompact(grossRevenueActual, dc)}
          subDetail={isCombined ? regionSub(formatCurrency(uk.gross_revenue_native, 'GBP'), formatCurrency(fr.gross_revenue_native, 'EUR')) : undefined}
          tooltip="What guests paid, before OTA commission and costs. Check-ins from January to the end of this month, same basis as the P&L page."
        />
        <KpiCard
          label="Net Revenue"
          value={formatCompact(netRevenueActual, dc)}
          subDetail={isCombined ? regionSub(formatCurrency(uk.net_revenue_native, 'GBP'), formatCurrency(fr.net_revenue_native, 'EUR')) : undefined}
          tooltip="Booking volume minus OTA commission, management fees, cleaning and repairs (what the management statements deduct)."
        />
        <KpiCard
          label="Gross Margin"
          value={`${formatCompact(grossMarginActual, dc)} (${grossMarginPctActual.toFixed(1)}%)`}
          subDetail={isCombined ? regionSub(formatCurrency(uk.gross_margin_native, 'GBP'), formatCurrency(fr.gross_margin_native, 'EUR')) : undefined}
          tooltip="Net revenue minus the fixed property costs: rent, mortgage (interest and capital), utilities, council tax or taxe fonciere, insurance, service charge. % of booking volume."
        />
        <KpiCard
          label="Net Margin"
          value={`${formatCompact(netResultActual, dc)} (${netMarginPctActual.toFixed(1)}%)`}
          subDetail={isCombined ? regionSub(formatCurrency(uk.net_margin_native, 'GBP'), formatCurrency(fr.net_margin_native, 'EUR')) : undefined}
          tooltip="Gross margin minus company overheads (payroll and subscriptions such as software and accountants) of the portfolio and holding companies. % of booking volume."
        />
      </div>

      {/* Row 2: Occupancy, ADR, RevPAR, Properties */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Occupancy"
          value={`${(occupancy ?? 0).toFixed(0)}%`}
          subDetail={isCombined ? regionSub(`${(uk.occupancy ?? 0).toFixed(0)}%`, `${(fr.occupancy ?? 0).toFixed(0)}%`) : undefined}
          tooltip="Time-weighted average occupancy across properties."
        />
        <KpiCard
          label="ADR"
          value={formatCompact(adr, dc)}
          subDetail={isCombined ? regionSub(formatCompact(uk.adr_native ?? 0, 'GBP'), formatCompact(fr.adr_native ?? 0, 'EUR')) : undefined}
          tooltip="Average Daily Rate: net stay revenue divided by nights sold."
        />
        <KpiCard
          label="RevPAR"
          value={formatCompact(revpar, dc)}
          subDetail={isCombined ? regionSub(formatCompact(uk.revpar_native ?? 0, 'GBP'), formatCompact(fr.revpar_native ?? 0, 'EUR')) : undefined}
          tooltip="Revenue Per Available Room-night: net stay revenue divided by available nights."
        />
        <KpiCard
          label="Properties"
          value={String(propertiesCount ?? 0)}
          subDetail={isCombined ? regionSub(String(uk.properties_count ?? 0), String(fr.properties_count ?? 0)) : undefined}
          tooltip="Number of active properties with revenue or booked nights in the period."
        />
      </div>
    </div>
  );
}

// ─── All Tab Content ─────────────────────────────────────────────────────────


function AllTabContent({ derived, nativeTotals, displayCurrency, forecastByMonth, year, priorYearMonthly }) {
  const dc = displayCurrency;
  const { totals, monthlyByRegion, monthlyCombined, ytdCombined, fx } = derived;
  const { uk, fr, combined } = totals;
  const eurGbp = fx.EUR_GBP;
  const hasForecast = forecastByMonth && Object.keys(forecastByMonth).length > 0;

  // Shared actuals/forecast boundary (single source of truth for all 5 charts).
  // Current month is the last solid actuals point; months strictly after the
  // current month are forecast. Matches actuals_cutoff_date (first of next
  // month, exclusive). See forecastChartUtils.jsx.
  const boundary = useMemo(() => getActualsForecastBoundary(), []);
  const isCurrentYear = year === boundary.currentMonthKey.substring(0, 4) * 1;
  // For past years, all 12 months are actuals
  const lastActualsIdx = isCurrentYear ? boundary.lastActualsMonthIdx : 11;
  // Chart data key labels depend on display currency
  const ukKey = dc === 'EUR' ? 'UK (EUR)' : 'UK (GBP)';
  const frKey = dc === 'EUR' ? 'FR (EUR)' : 'FR (GBP)';
  // The current month is fully actual on every Portfolio chart (Fan,
  // 2026-10-25 one-basis rule; 2026-10-28: the dashed light band for its
  // still-to-come bookings is wrong). Never split it with
  // markCurrentMonthPartial here.

  // Build chart data: merge UK + FR monthly
  const revenueChartDataBase = useMemo(() => {
    const ukRevKey = dc === 'EUR' ? 'revenue_eur' : 'revenue_gbp';
    const frRevKey = dc === 'EUR' ? 'revenue_eur' : 'revenue_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      const frm = monthlyByRegion.fr[i];
      const ukRev = ukm[ukRevKey] || 0;
      const frRev = frm?.[frRevKey] || 0;
      // Prior year: combined UK + FR in display currency (null if no bookings)
      const lyUkBookings = priorYearMonthly?.uk?.[i]?.bookings || 0;
      const lyFrBookings = priorYearMonthly?.fr?.[i]?.bookings || 0;
      const lyUk = priorYearMonthly?.uk?.[i]?.[ukRevKey] || 0;
      const lyFr = priorYearMonthly?.fr?.[i]?.[frRevKey] || 0;
      const lyHasData = lyUkBookings + lyFrBookings > 0;
      return {
        month: ukm.label,
        [ukKey]: ukRev,
        [frKey]: frRev,
        _total: ukRev + frRev,
        _priorYear: lyHasData ? lyUk + lyFr : null,
        // Keep native-currency originals for tooltip cross-reference
        // In EUR mode, revenue_gbp is undefined; recover GBP native via reverse FX
        _ukNative: dc === 'EUR' ? ukRev * eurGbp : ukRev,
        _frNative: dc === 'EUR' ? frRev : (frm?.revenue_eur || 0),
        _ukNativeCur: 'GBP',
        _frNativeCur: 'EUR',
      };
    });
  }, [monthlyByRegion, dc, ukKey, frKey, eurGbp, priorYearMonthly]);

  // Merge forecast into revenue chart data (per-region breakdown from endpoint)
  const revenueChartData = useMemo(() => {
    const merged = mergeWithForecast(revenueChartDataBase, forecastByMonth, (fm) => {
      const ukGbp = fm.revenue_uk_gbp || 0;
      const frGbp = fm.revenue_fr_gbp || 0;
      const displayUk = dc === 'EUR' && eurGbp > 0 ? ukGbp / eurGbp : ukGbp;
      const displayFr = dc === 'EUR' && eurGbp > 0 ? frGbp / eurGbp : frGbp;
      return { [ukKey]: displayUk, [frKey]: displayFr, _total: displayUk + displayFr };
    }, year);
    return merged;
  }, [revenueChartDataBase, forecastByMonth, dc, eurGbp, ukKey, frKey, year]);

  // Gross revenue chart data (parallel to net, reads gross_revenue_gbp/eur)
  const grossRevenueChartDataBase = useMemo(() => {
    const ukGrossKey = dc === 'EUR' ? 'gross_revenue_eur' : 'gross_revenue_gbp';
    const frGrossKey = dc === 'EUR' ? 'gross_revenue_eur' : 'gross_revenue_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      const frm = monthlyByRegion.fr[i];
      const ukGross = ukm[ukGrossKey] || 0;
      const frGross = frm?.[frGrossKey] || 0;
      // Prior year: combined UK + FR in display currency (null if no bookings)
      const lyUkBookings = priorYearMonthly?.uk?.[i]?.bookings || 0;
      const lyFrBookings = priorYearMonthly?.fr?.[i]?.bookings || 0;
      const lyUk = priorYearMonthly?.uk?.[i]?.[ukGrossKey] || 0;
      const lyFr = priorYearMonthly?.fr?.[i]?.[frGrossKey] || 0;
      const lyHasData = lyUkBookings + lyFrBookings > 0;
      return {
        month: ukm.label,
        [ukKey]: ukGross,
        [frKey]: frGross,
        _total: ukGross + frGross,
        _priorYear: lyHasData ? lyUk + lyFr : null,
      };
    });
  }, [monthlyByRegion, dc, ukKey, frKey, priorYearMonthly]);

  // Merge forecast into gross revenue chart data (same pattern as net revenue)
  const grossRevenueChartData = useMemo(() => {
    const merged = mergeWithForecast(grossRevenueChartDataBase, forecastByMonth, (fm) => {
      const ukGbp = fm.gross_revenue_uk_gbp || 0;
      const frGbp = fm.gross_revenue_fr_gbp || 0;
      const displayUk = dc === 'EUR' && eurGbp > 0 ? ukGbp / eurGbp : ukGbp;
      const displayFr = dc === 'EUR' && eurGbp > 0 ? frGbp / eurGbp : frGbp;
      return { [ukKey]: displayUk, [frKey]: displayFr, _total: displayUk + displayFr };
    }, year);
    return merged;
  }, [grossRevenueChartDataBase, forecastByMonth, dc, eurGbp, ukKey, frKey, year]);

  // Occupancy chart: show actuals through the current month (inclusive),
  // null for months beyond. Uses the shared boundary, not a data-driven
  // "last month with revenue" scan, which created a dead zone when the
  // current month had stay-month activity but no check-in-month revenue.
  const occupancyChartDataBase = useMemo(() => {
    return monthlyByRegion.uk.map((ukm, i) => ({
      month: ukm.label,
      UK: i <= lastActualsIdx ? Math.round(ukm.occupancy_pct) : null,
      FR: i <= lastActualsIdx ? Math.round(monthlyByRegion.fr[i]?.occupancy_pct || 0) : null,
      All: i <= lastActualsIdx ? Math.round(monthlyCombined[i].occupancy_pct) : null,
    }));
  }, [monthlyByRegion, monthlyCombined, lastActualsIdx]);

  // Merge forecast into occupancy chart data (per-region from endpoint)
  const occupancyChartDataMerged = useMemo(() => {
    return mergeWithForecast(occupancyChartDataBase, forecastByMonth, (fm) => {
      return {
        UK: fm.occupancy_uk_pct != null ? Math.round(fm.occupancy_uk_pct) : null,
        FR: fm.occupancy_fr_pct != null ? Math.round(fm.occupancy_fr_pct) : null,
        All: fm.occupancy_pct != null ? Math.round(fm.occupancy_pct) : null,
      };
    }, year);
  }, [occupancyChartDataBase, forecastByMonth, year]);
  const occupancyChartData = useMemo(
    () => splitForecastSeries(occupancyChartDataMerged, ['UK', 'FR', 'All']),
    [occupancyChartDataMerged]
  );

  const frRateLabel = `FR (${dc})`;
  const nightlyRateChartDataBase = useMemo(() => {
    const ukRateKey = dc === 'EUR' ? 'avg_nightly_rate_eur' : 'avg_nightly_rate_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      if (i > lastActualsIdx) return { month: ukm.label, UK: null, [frRateLabel]: null, All: null };
      const frm = monthlyByRegion.fr[i];
      const frRateKey = dc === 'EUR' ? 'avg_nightly_rate_eur' : 'avg_nightly_rate_gbp';
      const frRate = frm?.[frRateKey] || null;
      return {
        month: ukm.label,
        'UK': ukm[ukRateKey] || null,
        [frRateLabel]: frRate,
        'All': monthlyCombined[i]?.stay_nights > 0 ? monthlyCombined[i].avg_nightly_rate_gbp : null,
      };
    });
  }, [monthlyByRegion, monthlyCombined, dc, frRateLabel, lastActualsIdx]);

  // Merge forecast into ADR chart data (per-region from endpoint)
  const nightlyRateChartDataMerged = useMemo(() => {
    return mergeWithForecast(nightlyRateChartDataBase, forecastByMonth, (fm) => {
      const ukAdr = fm.adr_uk_gbp;
      const frAdr = fm.adr_fr_gbp;
      const allAdr = fm.adr_gbp;
      // Convert to display currency if needed
      const toDisplay = (v) => v != null && dc === 'EUR' && eurGbp > 0 ? v / eurGbp : v;
      return {
        UK: toDisplay(ukAdr) ?? null,
        [frRateLabel]: toDisplay(frAdr) ?? null,
        All: toDisplay(allAdr) ?? null,
      };
    }, year);
  }, [nightlyRateChartDataBase, forecastByMonth, dc, eurGbp, frRateLabel, year]);
  const nightlyRateChartData = useMemo(
    () => splitForecastSeries(nightlyRateChartDataMerged, ['UK', frRateLabel, 'All']),
    [nightlyRateChartDataMerged, frRateLabel]
  );

  // Net RevPAR chart data (parallel to nightly rate, reads revpar_gbp/eur)
  const revparChartDataBase = useMemo(() => {
    const ukRevparKey = dc === 'EUR' ? 'revpar_eur' : 'revpar_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      if (i > lastActualsIdx) return { month: ukm.label, UK: null, [frRateLabel]: null, All: null };
      const frm = monthlyByRegion.fr[i];
      const frRevparKey = dc === 'EUR' ? 'revpar_eur' : 'revpar_gbp';
      const frRevpar = frm?.[frRevparKey] || null;
      return {
        month: ukm.label,
        'UK': ukm[ukRevparKey] || null,
        [frRateLabel]: frRevpar,
        'All': monthlyCombined[i]?.available_nights > 0 ? monthlyCombined[i].revpar_gbp : null,
      };
    });
  }, [monthlyByRegion, monthlyCombined, dc, frRateLabel, lastActualsIdx]);

  const revparChartDataMerged = useMemo(() => {
    return mergeWithForecast(revparChartDataBase, forecastByMonth, (fm) => {
      const ukRevpar = fm.revpar_uk_gbp;
      const frRevpar = fm.revpar_fr_gbp;
      const allRevpar = fm.revpar_gbp;
      const toDisplay = (v) => v != null && dc === 'EUR' && eurGbp > 0 ? v / eurGbp : v;
      return {
        UK: toDisplay(ukRevpar) ?? null,
        [frRateLabel]: toDisplay(frRevpar) ?? null,
        All: toDisplay(allRevpar) ?? null,
      };
    }, year);
  }, [revparChartDataBase, forecastByMonth, dc, eurGbp, frRateLabel, year]);

  const revparChartData = useMemo(
    () => splitForecastSeries(revparChartDataMerged, ['UK', frRateLabel, 'All']),
    [revparChartDataMerged, frRateLabel]
  );

  // Rate/RevPAR toggle state for the combined chart
  const [rateChartMetric, setRateChartMetric] = useState('adr');

  // Margin bar chart data: Net as stacked bars, Gross as line overlay
  const marginChartDataBase = useMemo(() => {
    const ukNmKey = dc === 'EUR' ? 'net_margin_eur' : 'net_margin_gbp';
    const frNmKey = dc === 'EUR' ? 'net_margin_eur' : 'net_margin_gbp';
    const ukGmKey = dc === 'EUR' ? 'gross_margin_eur' : 'gross_margin_gbp';
    const frGmKey = dc === 'EUR' ? 'gross_margin_eur' : 'gross_margin_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      const frm = monthlyByRegion.fr[i];
      const ukVal = ukm[ukNmKey] || 0;
      const frVal = frm?.[frNmKey] || 0;
      const ukGross = ukm[ukGmKey] || 0;
      const frGross = frm?.[frGmKey] || 0;
      return {
        month: ukm.label,
        [ukKey]: ukVal,
        [frKey]: frVal,
        _total: ukVal + frVal,
        'Gross Margin': ukGross + frGross,
        _ukNative: dc === 'EUR' ? ukVal * eurGbp : ukVal,
        _frNative: dc === 'EUR' ? frVal : (frm?.net_margin_eur || 0),
        _ukNativeCur: 'GBP',
        _frNativeCur: 'EUR',
        // Estimated management-statement costs already inside the bars
        // (actual months whose CWC statement is not in yet, A134).
        _estStatementCosts: (() => {
          const estGbp = (ukm.estimated_statement_costs_gbp || 0) + (frm?.estimated_statement_costs_gbp || 0);
          if (!estGbp) return null;
          return dc === 'EUR' && eurGbp > 0 ? estGbp / eurGbp : estGbp;
        })(),
      };
    });
  }, [monthlyByRegion, dc, ukKey, frKey, eurGbp]);

  // Merge forecast into margin chart data (per-region breakdown from endpoint)
  const marginChartData = useMemo(() => {
    // Forecast net margin: since 2026-10-25 ("Align forecasts") the forecast
    // endpoint's margin for months after this one IS the P&L forecast net
    // result (scheduled costs + estimated statement costs, backend
    // routes/forecasts._align_portfolio_with_pnl), the same figure as the
    // P&L page's Forward row. Read as-is, never re-derived here.
    const merged = mergeWithForecast(marginChartDataBase, forecastByMonth, (fm) => {
      const ukGbp = fm.margin_uk_gbp || 0;
      const frGbp = fm.margin_fr_gbp || 0;
      const displayUk = dc === 'EUR' && eurGbp > 0 ? ukGbp / eurGbp : ukGbp;
      const displayFr = dc === 'EUR' && eurGbp > 0 ? frGbp / eurGbp : frGbp;
      return { [ukKey]: displayUk, [frKey]: displayFr, _total: displayUk + displayFr, 'Gross Margin': undefined };
    }, year);
    return merged;
  }, [marginChartDataBase, forecastByMonth, dc, eurGbp, ukKey, frKey, year]);

  // ── Average Revenue after OTA per Property ──────────────────────────────────────
  // Denominator: properties with first_listed_date <= end of that month (active/onboarded).
  const activePropertyCount = useMemo(() => {
    const result = { uk: Array(12).fill(0), fr: Array(12).fill(0) };
    for (const p of derived.propertiesYtd) {
      if (!p.first_listed_date || !p.region) continue;
      const region = p.region;
      if (region !== 'uk' && region !== 'fr') continue;
      for (let mi = 0; mi < 12; mi++) {
        const monthEnd = `${year}-${String(mi + 1).padStart(2, '0')}-31`;
        if (p.first_listed_date.substring(0, 10) <= monthEnd) {
          result[region][mi]++;
        }
      }
    }
    return result;
  }, [derived.propertiesYtd, year]);

  const avgRevPerPropertyBase = useMemo(() => {
    const revKey = dc === 'EUR' ? 'revenue_eur' : 'revenue_gbp';
    return monthlyByRegion.uk.map((ukm, i) => {
      if (i > lastActualsIdx) return { month: ukm.label, UK: null, FR: null };
      const frm = monthlyByRegion.fr[i];
      const ukCount = activePropertyCount.uk[i];
      const frCount = activePropertyCount.fr[i];
      const ukRev = ukm[revKey] || 0;
      const frRev = frm?.[revKey] || 0;
      return {
        month: ukm.label,
        UK: ukCount > 0 ? ukRev / ukCount : null,
        FR: frCount > 0 ? frRev / frCount : null,
      };
    });
  }, [monthlyByRegion, dc, activePropertyCount, lastActualsIdx]);

  const avgRevPerPropertyMerged = useMemo(() => {
    return mergeWithForecast(avgRevPerPropertyBase, forecastByMonth, (fm) => {
      const ukGbp = fm.revenue_uk_gbp || 0;
      const frGbp = fm.revenue_fr_gbp || 0;
      const displayUk = dc === 'EUR' && eurGbp > 0 ? ukGbp / eurGbp : ukGbp;
      const displayFr = dc === 'EUR' && eurGbp > 0 ? frGbp / eurGbp : frGbp;
      const mk = fm.month;
      const mi = mk ? parseInt(mk.split('-')[1], 10) - 1 : -1;
      const ukCount = mi >= 0 ? activePropertyCount.uk[mi] : 0;
      const frCount = mi >= 0 ? activePropertyCount.fr[mi] : 0;
      return {
        UK: ukCount > 0 ? displayUk / ukCount : null,
        FR: frCount > 0 ? displayFr / frCount : null,
      };
    }, year);
  }, [avgRevPerPropertyBase, forecastByMonth, dc, eurGbp, activePropertyCount, year]);

  const avgRevPerPropertyData = useMemo(
    () => splitForecastSeries(avgRevPerPropertyMerged, ['UK', 'FR']),
    [avgRevPerPropertyMerged]
  );

  return (
    <div className="space-y-6">
      {/* KPI cards: star (Gross Revenue) + two rows of four */}
      <PortfolioKpiCards scope="combined" derived={derived} displayCurrency={dc} nativeTotals={nativeTotals} />

      {/* Monthly Gross Revenue - stacked bar chart (pre-commission) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Booking Volume</CardTitle>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={grossRevenueChartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, dc)} />
              <Tooltip content={<StackedBarTooltip dc={dc} ukKey={ukKey} frKey={frKey} priorYearLabel={`${year - 1}`} />} />
              <Legend />
              <Bar dataKey={ukKey} stackId="gross" fill={REGION_COLORS.UK} shape={<ForecastBarShape data={grossRevenueChartData} />}>
                <LabelList
                  dataKey={ukKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Bar dataKey={frKey} stackId="gross" fill={REGION_COLORS.FR} radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={grossRevenueChartData} />}>
                <LabelList
                  dataKey={frKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
                <LabelList
                  position="top"
                  content={(props) => {
                    const { x, y, width, index } = props;
                    const entry = grossRevenueChartData[index];
                    const total = entry?._total;
                    if (!total) return null;
                    return (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                        {formatCompact(total, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Line type="monotone" dataKey="_priorYear" stroke={PRIOR_YEAR_COLOR} strokeWidth={1.5} dot={false} name={`${year - 1}`} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Booking Volume:</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
              <span>FR</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.france.gross_revenue_eur, 'EUR')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
              <span>UK</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.uk.gross_revenue_gbp, 'GBP')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.COMBINED }} />
              <span>Combined</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.combined.gross_revenue_gbp, dc)}</span>
            </span>
          </div>
          <ForecastTotalsFooter
            includeCurrentMonthRemainder={false}
            data={grossRevenueChartData}
            series={[
              { key: frKey, label: 'FR', color: REGION_COLORS.FR },
              { key: ukKey, label: 'UK', color: REGION_COLORS.UK },
            ]}
            currency={dc}
            label="Booking Volume"
          />
        </CardContent>
      </Card>

      {/* Monthly Revenue after OTA */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Revenue after OTA</CardTitle>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={revenueChartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, dc)} />
              <Tooltip content={<StackedBarTooltip dc={dc} ukKey={ukKey} frKey={frKey} priorYearLabel={`${year - 1}`} />} />
              <Legend />

              <Bar dataKey={ukKey} stackId="rev" fill={REGION_COLORS.UK} shape={<ForecastBarShape data={revenueChartData} />}>
                <LabelList
                  dataKey={ukKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Bar dataKey={frKey} stackId="rev" fill={REGION_COLORS.FR} radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={revenueChartData} />}>
                <LabelList
                  dataKey={frKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
                <LabelList
                  position="top"
                  content={(props) => {
                    const { x, y, width, index } = props;
                    const entry = revenueChartData[index];
                    const total = entry?._total;
                    if (!total) return null;
                    return (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                        {formatCompact(total, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Line type="monotone" dataKey="_priorYear" stroke={PRIOR_YEAR_COLOR} strokeWidth={1.5} dot={false} name={`${year - 1}`} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Revenue after OTA:</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
              <span>FR</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.france.revenue_eur, 'EUR')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
              <span>UK</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.uk.revenue_gbp, 'GBP')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.COMBINED }} />
              <span>Combined</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.combined.revenue_gbp, dc)}</span>
            </span>
          </div>
          <ForecastTotalsFooter
            includeCurrentMonthRemainder={false}
            data={revenueChartData}
            series={[
              { key: frKey, label: 'FR', color: REGION_COLORS.FR },
              { key: ukKey, label: 'UK', color: REGION_COLORS.UK },
            ]}
            currency={dc}
            label="Revenue after OTA"
          />
        </CardContent>
      </Card>

      {/* Net & Gross Margin chart: stacked bars = Net (by region), line = Gross */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Margin ({dc})</CardTitle>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            {/* stackOffset="sign": a negative region (UK in Jan) goes below
                zero and a positive one above it. The default stacked FR on
                top of UK's negative bar, so FR's +17k was drawn under zero. */}
            <ComposedChart data={marginChartData} stackOffset="sign" margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, dc)} />
              <Tooltip content={<StackedBarTooltip dc={dc} ukKey={ukKey} frKey={frKey} grossMarginKey="Gross Margin" />} />
              <Legend />

              <Bar dataKey={ukKey} stackId="margin" fill={REGION_COLORS.UK} shape={<ForecastBarShape data={marginChartData} />}>
                <LabelList
                  dataKey={ukKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || Math.abs(value) < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Bar dataKey={frKey} stackId="margin" fill={REGION_COLORS.FR} radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={marginChartData} />}>
                <LabelList
                  dataKey={frKey}
                  position="center"
                  content={({ x, y, width, height, value }) => {
                    if (!value || Math.abs(value) < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
                        {formatCompact(value, dc)}
                      </text>
                    );
                  }}
                />
                <LabelList
                  position="top"
                  content={(props) => {
                    const { x, y, width, index } = props;
                    const entry = marginChartData[index];
                    const total = entry?._total;
                    if (!total) return null;
                    return (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                        {formatCompact(total, dc)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Line
                type="monotone"
                dataKey="Gross Margin"
                stroke="#F59E0B"
                strokeWidth={2}
                dot={{ r: 3, fill: '#F59E0B' }}
                connectNulls={false}
                name="Gross Margin"
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Gross Margin:</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              <span>Combined</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.combined.gross_margin_gbp, dc)}</span>
            </span>
          </div>
          <div className="mt-1 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Net Margin:</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
              <span>FR</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.france.net_margin_eur, 'EUR')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
              <span>UK</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.uk.net_margin_gbp, 'GBP')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.COMBINED }} />
              <span>Combined</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(ytdCombined.combined.net_margin_gbp, dc)}</span>
            </span>
          </div>
          <ForecastTotalsFooter
            includeCurrentMonthRemainder={false}
            data={marginChartData}
            series={[
              { key: frKey, label: 'FR', color: REGION_COLORS.FR },
              { key: ukKey, label: 'UK', color: REGION_COLORS.UK },
            ]}
            currency={dc}
            label="Net Margin"
          />
        </CardContent>
      </Card>

      {/* Occupancy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Occupancy</CardTitle>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={occupancyChartData} margin={{ top: 20, right: 40, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => `${v}%`} domain={[0, 100]} />
              <Tooltip formatter={(v) => v != null ? `${v}%` : ''} />
              <Legend />

              <Line type="monotone" dataKey="UK" stroke={REGION_COLORS.UK} strokeWidth={2} dot={{ r: 4 }} connectNulls>
                <LabelList dataKey="UK" position="top" formatter={v => `${Math.round(v)}%`} fill={REGION_COLORS.UK} fontSize={11} offset={8} />
              </Line>
              <Line type="monotone" dataKey="FR" stroke={REGION_COLORS.FR} strokeWidth={2} dot={{ r: 4 }} connectNulls>
                <LabelList dataKey="FR" position="bottom" formatter={v => `${Math.round(v)}%`} fill={REGION_COLORS.FR} fontSize={11} offset={8} />
              </Line>
              <Line type="monotone" dataKey="All" stroke={REGION_COLORS.COMBINED} strokeWidth={2} dot={false} connectNulls>
                <LabelList
                  dataKey="All"
                  content={(props) => {
                    const { x, y, value, index } = props;
                    if (value == null) return null;
                    const lastIdx = occupancyChartData.reduce((last, d, i) => d.All != null ? i : last, -1);
                    if (index !== lastIdx) return null;
                    return (
                      <text x={x + 8} y={y - 6} fontSize={11} fontWeight={600} fill={REGION_COLORS.COMBINED}>
                        {formatPercent(value)}
                      </text>
                    );
                  }}
                />
              </Line>
              {/* Forecast dashed lines */}
              <Line type="monotone" dataKey="UK_forecast" stroke={REGION_COLORS.UK} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                <LabelList dataKey="UK_forecast" content={forecastLabelContent(occupancyChartData, { formatter: v => `${Math.round(v)}%`, fill: REGION_COLORS.UK })} />
              </Line>
              <Line type="monotone" dataKey="FR_forecast" stroke={REGION_COLORS.FR} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                <LabelList dataKey="FR_forecast" content={forecastLabelContent(occupancyChartData, { formatter: v => `${Math.round(v)}%`, fill: REGION_COLORS.FR, position: 'bottom' })} />
              </Line>
              <Line type="monotone" dataKey="All_forecast" stroke={REGION_COLORS.COMBINED} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                <LabelList dataKey="All_forecast" content={forecastLabelContent(occupancyChartData, { formatter: v => `${Math.round(v)}%`, fill: REGION_COLORS.COMBINED })} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Occupancy:</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
              <span>FR</span>
              <span className="font-mono font-semibold text-foreground">{formatPercent(ytdCombined.france.occupancy_pct)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
              <span>UK</span>
              <span className="font-mono font-semibold text-foreground">{formatPercent(ytdCombined.uk.occupancy_pct)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.COMBINED }} />
              <span>All</span>
              <span className="font-mono font-semibold text-foreground">{formatPercent(ytdCombined.combined.occupancy_pct)}</span>
            </span>
          </div>
          <ForecastTotalsFooter
            includeCurrentMonthRemainder={false}
            data={occupancyChartData}
            series={[
              { key: 'FR', label: 'FR', color: REGION_COLORS.FR },
              { key: 'UK', label: 'UK', color: REGION_COLORS.UK },
              { key: 'All', label: 'All', color: REGION_COLORS.COMBINED },
            ]}
            mode="avg"
            label="Occupancy (avg)"
            format={(v) => formatPercent(v)}
          />
        </CardContent>
      </Card>

      {/* Avg Net Nightly Rate / Net RevPAR (toggled) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {rateChartMetric === 'adr' ? `Average Net Nightly Rate (${dc})` : `Net RevPAR (${dc})`}
            </CardTitle>
            <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
              {[
                { key: 'adr', label: 'Nightly Rate' },
                { key: 'revpar', label: 'RevPAR' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={cn(
                    'px-2.5 py-1 text-xs font-semibold rounded transition-all',
                    rateChartMetric === key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  onClick={() => setRateChartMetric(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {rateChartMetric === 'adr'
              ? 'Net revenue per night, prorated across actual stay months'
              : 'Net stay revenue / available nights (calendar basis)'}
          </p>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          {(() => {
            const activeData = rateChartMetric === 'adr' ? nightlyRateChartData : revparChartData;
            return (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={activeData} margin={{ top: 20, right: 40, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => `${currencySymbol(dc)}${Math.round(v)}`} />
                    <Tooltip formatter={(v) => v != null ? formatCurrency(v, dc) : ''} />
                    <Legend />

                    <Line type="monotone" dataKey="UK" stroke={REGION_COLORS.UK} strokeWidth={2} dot={{ r: 4 }} connectNulls>
                      <LabelList dataKey="UK" position="top" formatter={v => `${currencySymbol(dc)}${Math.round(v)}`} fill={REGION_COLORS.UK} fontSize={11} offset={8} />
                    </Line>
                    <Line type="monotone" dataKey={frRateLabel} stroke={REGION_COLORS.FR} strokeWidth={2} dot={{ r: 4 }} connectNulls>
                      <LabelList dataKey={frRateLabel} position="bottom" formatter={v => `${currencySymbol(dc)}${Math.round(v)}`} fill={REGION_COLORS.FR} fontSize={11} offset={8} />
                    </Line>
                    <Line type="monotone" dataKey="All" stroke={REGION_COLORS.COMBINED} strokeWidth={2} dot={false} connectNulls>
                      <LabelList
                        dataKey="All"
                        content={(props) => {
                          const { x, y, value, index } = props;
                          if (value == null) return null;
                          const lastIdx = activeData.reduce((last, d, i) => d.All != null ? i : last, -1);
                          if (index !== lastIdx) return null;
                          return (
                            <text x={x + 8} y={y - 6} fontSize={11} fontWeight={600} fill={REGION_COLORS.COMBINED}>
                              {formatCompact(value, dc)}
                            </text>
                          );
                        }}
                      />
                    </Line>
                    {/* Forecast dashed lines */}
                    <Line type="monotone" dataKey="UK_forecast" stroke={REGION_COLORS.UK} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                      <LabelList dataKey="UK_forecast" content={forecastLabelContent(activeData, { formatter: v => `${currencySymbol(dc)}${Math.round(v)}`, fill: REGION_COLORS.UK })} />
                    </Line>
                    <Line type="monotone" dataKey={`${frRateLabel}_forecast`} stroke={REGION_COLORS.FR} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                      <LabelList dataKey={`${frRateLabel}_forecast`} content={forecastLabelContent(activeData, { formatter: v => `${currencySymbol(dc)}${Math.round(v)}`, fill: REGION_COLORS.FR, position: 'bottom' })} />
                    </Line>
                    <Line type="monotone" dataKey="All_forecast" stroke={REGION_COLORS.COMBINED} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                      <LabelList dataKey="All_forecast" content={forecastLabelContent(activeData, { formatter: v => `${currencySymbol(dc)}${Math.round(v)}`, fill: REGION_COLORS.COMBINED })} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
                  <span>{rateChartMetric === 'adr' ? 'YTD Avg Net Nightly Rate:' : 'YTD Net RevPAR:'}</span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
                    <span>FR</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCompact(rateChartMetric === 'adr' ? ytdCombined.france.avg_nightly_rate_gbp : (ytdCombined.france.revpar_gbp || 0), dc)}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
                    <span>UK</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCompact(rateChartMetric === 'adr' ? ytdCombined.uk.avg_nightly_rate_gbp : (ytdCombined.uk.revpar_gbp || 0), dc)}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.COMBINED }} />
                    <span>All</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCompact(rateChartMetric === 'adr' ? ytdCombined.combined.avg_nightly_rate_gbp : (ytdCombined.combined.revpar_gbp || 0), dc)}
                    </span>
                  </span>
                </div>
                <ForecastTotalsFooter
                  includeCurrentMonthRemainder={false}
                  data={activeData}
                  series={[
                    { key: frRateLabel, label: 'FR', color: REGION_COLORS.FR },
                    { key: 'UK', label: 'UK', color: REGION_COLORS.UK },
                    { key: 'All', label: 'All', color: REGION_COLORS.COMBINED },
                  ]}
                  mode="avg"
                  currency={dc}
                  label={rateChartMetric === 'adr' ? 'Avg Net Nightly Rate' : 'Net RevPAR'}
                />
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Average Revenue after OTA per Property */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Average Revenue after OTA per Property ({dc})</CardTitle>
          <p className="text-xs text-muted-foreground">Monthly net revenue &#247; active properties (onboarded by that month)</p>
          <ForecastCaveat show={hasForecast} />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={avgRevPerPropertyData} margin={{ top: 20, right: 40, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, dc)} />
              <Tooltip formatter={(v) => v != null ? formatCurrency(v, dc) : ''} />
              <Legend />
              <Line type="monotone" dataKey="UK" stroke={REGION_COLORS.UK} strokeWidth={2} dot={{ r: 4 }} connectNulls={false}>
                <LabelList dataKey="UK" position="top" formatter={v => formatCompact(v, dc)} fill={REGION_COLORS.UK} fontSize={11} offset={8} />
              </Line>
              <Line type="monotone" dataKey="FR" stroke={REGION_COLORS.FR} strokeWidth={2} dot={{ r: 4 }} connectNulls={false}>
                <LabelList dataKey="FR" position="bottom" formatter={v => formatCompact(v, dc)} fill={REGION_COLORS.FR} fontSize={11} offset={8} />
              </Line>
              <Line type="monotone" dataKey="UK_forecast" stroke={REGION_COLORS.UK} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                <LabelList dataKey="UK_forecast" content={forecastLabelContent(avgRevPerPropertyData, { formatter: v => formatCompact(v, dc), fill: REGION_COLORS.UK })} />
              </Line>
              <Line type="monotone" dataKey="FR_forecast" stroke={REGION_COLORS.FR} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls legendType="none">
                <LabelList dataKey="FR_forecast" content={forecastLabelContent(avgRevPerPropertyData, { formatter: v => formatCompact(v, dc), fill: REGION_COLORS.FR, position: 'bottom' })} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>YTD Avg per Property:</span>
            {(() => {
              // Property count denominator for YTD avg uses the current month
              // (most up-to-date onboarded count), not the chart actuals boundary.
              const propCountIdx = boundary.lastActualsMonthIdx;
              const frCount = activePropertyCount.fr[propCountIdx];
              const ukCount = activePropertyCount.uk[propCountIdx];
              return (<>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.FR }} />
                  <span>FR</span>
                  <span className="font-mono font-semibold text-foreground">
                    {frCount > 0 ? formatCompact(fr.revenue / frCount, dc) : '-'}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: REGION_COLORS.UK }} />
                  <span>UK</span>
                  <span className="font-mono font-semibold text-foreground">
                    {ukCount > 0 ? formatCompact(uk.revenue / ukCount, dc) : '-'}
                  </span>
                </span>
              </>);
            })()}
          </div>
          <ForecastTotalsFooter
            includeCurrentMonthRemainder={false}
            data={avgRevPerPropertyData}
            series={[
              { key: 'FR', label: 'FR', color: REGION_COLORS.FR },
              { key: 'UK', label: 'UK', color: REGION_COLORS.UK },
            ]}
            mode="avg"
            currency={dc}
            label="Avg per Property"
          />
        </CardContent>
      </Card>

    </div>
  );
}

function StackedBarTooltip({ active, payload, label, dc, ukKey, frKey, priorYearLabel, grossMarginKey }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const ukVal = point[ukKey] || 0;
  const frVal = point[frKey] || 0;
  const total = point._total || 0;
  const priorYear = point._priorYear;

  // Native values for cross-reference (always GBP for UK, EUR for FR)
  const ukNative = point._ukNative;
  const frNative = point._frNative;
  const ukNativeCur = point._ukNativeCur;
  const frNativeCur = point._frNativeCur;

  // Show cross-reference line when the displayed currency differs from native
  const showUkNative = ukNativeCur && ukNativeCur !== dc && ukNative != null;
  const showFrNative = frNativeCur && frNativeCur !== dc && frNative != null;

  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm min-w-[220px]">
      <p className="font-semibold mb-2">{label}</p>
      {/* UK */}
      <div className="flex justify-between gap-6">
        <span style={{ color: REGION_COLORS.UK }}>{ukKey}</span>
        <span className="font-mono">{formatCurrency(ukVal, dc)}</span>
      </div>
      {showUkNative && (
        <div className="flex justify-between gap-6 text-xs text-muted-foreground">
          <span className="pl-3">{ukNativeCur} native</span>
          <span className="font-mono">{formatCurrency(ukNative, ukNativeCur)}</span>
        </div>
      )}
      {/* FR */}
      <div className="flex justify-between gap-6 mt-1">
        <span style={{ color: REGION_COLORS.FR }}>{frKey}</span>
        <span className="font-mono">{formatCurrency(frVal, dc)}</span>
      </div>
      {showFrNative && (
        <div className="flex justify-between gap-6 text-xs text-muted-foreground">
          <span className="pl-3">{frNativeCur} native</span>
          <span className="font-mono">{formatCurrency(frNative, frNativeCur)}</span>
        </div>
      )}
      {/* Combined (Net) */}
      <div className="flex justify-between gap-6 border-t pt-1 mt-2 font-medium">
        <span>{grossMarginKey ? 'Net' : 'Combined'} ({dc})</span>
        <span className="font-mono">{formatCurrency(total, dc)}</span>
      </div>
      {grossMarginKey && point[grossMarginKey] != null && (
        <div className="flex justify-between gap-6 mt-1 font-medium">
          <span style={{ color: '#F59E0B' }}>Gross ({dc})</span>
          <span className="font-mono">{formatCurrency(point[grossMarginKey], dc)}</span>
        </div>
      )}
      {priorYearLabel && priorYear != null && (
        <div className="flex justify-between gap-6 mt-1 text-muted-foreground">
          <span style={{ color: PRIOR_YEAR_COLOR }}>{priorYearLabel}</span>
          <span className="font-mono">{formatCurrency(priorYear, dc)}</span>
        </div>
      )}
      {point._estStatementCosts != null && (
        <div className="text-xs text-muted-foreground mt-2 max-w-[260px]">
          Includes {formatCurrency(point._estStatementCosts, dc)} estimated management fees,
          cleaning and repairs (management statement not in yet)
        </div>
      )}
    </div>
  );
}

/** Lightweight tooltip for country-tab bar charts. Filters out null values so
 *  the prior-year row hides on months with no data instead of showing "£0.00". */
function CountryBarTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm min-w-[180px]">
      <p className="font-semibold mb-2">{label}</p>
      {payload.filter(p => p.value != null).map((p, idx) => (
        <div key={idx} className="flex justify-between gap-6">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono">{formatCurrency(p.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Country Overview Sub-tab ────────────────────────────────────────────────

function CountryOverviewContent({ region, derived, displayCurrency, priorYearMonthly, year, forecastByMonth }) {
  const currency = displayCurrency;
  const sym = currencySymbol(currency);
  const totals = derived.totals[region];
  const channels = derived.channelBreakdownByRegion[region];
  const regionColor = region === 'uk' ? REGION_COLORS.UK : REGION_COLORS.FR;
  const eurGbp = derived.fx.EUR_GBP;

  const propertiesInRegion = useMemo(
    () => derived.propertiesYtd
      .filter(p => p.region === region)
      .sort((a, b) => b.revenue_native - a.revenue_native),
    [derived.propertiesYtd, region]
  );

  // Monthly aggregate totals for the region
  const regionMonthly = derived.monthlyByRegion[region];

  // Forecast overlay: uses the shared actuals/forecast boundary.
  // Current month is actuals; months after current month are forecast.
  const countryBoundary = useMemo(() => getActualsForecastBoundary(), []);
  const currentYear = countryBoundary.currentMonthKey.substring(0, 4) * 1;
  const hasForecast = year === currentYear && forecastByMonth && Object.keys(forecastByMonth).length > 0;
  // Heatmap total cells: whole forecast months only (the current month keeps
  // its full-month occupancy/ADR, which already include its forward nights).
  const forecastMonthStart = hasForecast ? countryBoundary.lastActualsMonthIdx + 1 : null;
  const forecastMonthEnd = hasForecast ? Math.min(countryBoundary.lastForecastMonthIdx, 11) : null;
  const forecastOccKey = region === 'uk' ? 'occupancy_uk_pct' : 'occupancy_fr_pct';
  const forecastAdrKey = region === 'uk' ? 'adr_uk_gbp' : 'adr_fr_gbp';
  // Convert forecast GBP value to display currency
  const toDisplayCurrency = (gbpVal) => {
    if (gbpVal == null) return null;
    if (currency === 'EUR' && eurGbp > 0) return gbpVal / eurGbp;
    return gbpVal;
  };

  // Country-tab revenue bar chart data (gross + net) with prior-year line
  const grossKey = currency === 'EUR' ? 'gross_revenue_eur' : 'gross_revenue_gbp';
  const netKey = currency === 'EUR' ? 'revenue_eur' : 'revenue_gbp';

  const countryGrossChartData = useMemo(() => {
    if (!regionMonthly) return [];
    return regionMonthly.map((m, i) => {
      const lyBookings = priorYearMonthly?.[region]?.[i]?.bookings || 0;
      const lyVal = priorYearMonthly?.[region]?.[i]?.[grossKey] || 0;
      return {
        month: m.label,
        revenue: m[grossKey] || 0,
        _priorYear: lyBookings > 0 ? lyVal : null,
      };
    });
  }, [regionMonthly, priorYearMonthly, region, grossKey]);

  const countryNetChartData = useMemo(() => {
    if (!regionMonthly) return [];
    return regionMonthly.map((m, i) => {
      const lyBookings = priorYearMonthly?.[region]?.[i]?.bookings || 0;
      const lyVal = priorYearMonthly?.[region]?.[i]?.[netKey] || 0;
      return {
        month: m.label,
        revenue: m[netKey] || 0,
        _priorYear: lyBookings > 0 ? lyVal : null,
      };
    });
  }, [regionMonthly, priorYearMonthly, region, netKey]);

  // Revenue-weighted nightly rate total for the heatmap (reads GBP-converted
  // keys from Commit 1 so FR EUR values are properly converted before weighting)
  const nightlyRateKey = currency === 'EUR' ? 'avg_nightly_rate_eur' : 'avg_nightly_rate_gbp';
  const nightlyRateYtdTotal = useMemo(() => {
    if (!regionMonthly) return null;
    let totalWeighted = 0, totalNights = 0;
    for (const m of regionMonthly) {
      const rate = m?.[nightlyRateKey];
      const nights = m?.stay_nights || 0;
      if (rate > 0 && nights > 0) {
        totalWeighted += rate * nights;
        totalNights += nights;
      }
    }
    return totalNights > 0 ? totalWeighted / totalNights : null;
  }, [regionMonthly, nightlyRateKey]);

  return (
    <div className="space-y-6">
      {/* KPI cards: star (Gross Revenue) + two rows of four */}
      <PortfolioKpiCards scope={region} derived={derived} displayCurrency={currency} />

      {/* Monthly Gross Revenue bar chart with prior-year line */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Booking Volume ({currency})</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={countryGrossChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, currency)} />
              <Tooltip content={<CountryBarTooltip currency={currency} />} />
              <Legend />
              <Bar dataKey="revenue" fill={regionColor} radius={[4, 4, 0, 0]} name={`${year}`}>
                <LabelList
                  dataKey="revenue"
                  position="top"
                  content={({ x, y, width, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                        {formatCompact(value, currency)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Line type="monotone" dataKey="_priorYear" stroke={PRIOR_YEAR_COLOR} strokeWidth={1.5} dot={false} name={`${year - 1}`} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Revenue after OTA bar chart with prior-year line */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Revenue after OTA ({currency})</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={countryNetChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={v => formatCompact(v, currency)} />
              <Tooltip content={<CountryBarTooltip currency={currency} />} />
              <Legend />
              <Bar dataKey="revenue" fill={regionColor} radius={[4, 4, 0, 0]} name={`${year}`}>
                <LabelList
                  dataKey="revenue"
                  position="top"
                  content={({ x, y, width, value }) => {
                    if (!value || value < 1000) return null;
                    return (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                        {formatCompact(value, currency)}
                      </text>
                    );
                  }}
                />
              </Bar>
              <Line type="monotone" dataKey="_priorYear" stroke={PRIOR_YEAR_COLOR} strokeWidth={1.5} dot={false} name={`${year - 1}`} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gross Revenue heatmap */}
      <PropertyMonthHeatmap
        properties={propertiesInRegion}
        valueAccessor={(p, mi) => p.monthly[mi]?.gross_revenue ?? null}
        colorScale="emerald"
        formatter={(v) => formatCompact(v, currency)}
        title={`Monthly Booking Volume (${currency})`}
        monthTotalAccessor={regionMonthly ? (mi) => {
          const m = regionMonthly[mi];
          return currency === 'EUR' ? (m?.gross_revenue_eur ?? null) : (m?.gross_revenue_gbp ?? null);
        } : undefined}
        monthTotalFormatter={(v) => formatCompact(v, currency)}
        ytdAccessor={(p) => p.gross_revenue_native}
        ytdTotal={totals.gross_revenue}
      />

      {/* Revenue after OTA heatmap */}
      <PropertyMonthHeatmap
        properties={propertiesInRegion}
        valueAccessor={(p, mi) => p.monthly[mi]?.revenue ?? null}
        colorScale="blue"
        formatter={(v) => formatCompact(v, currency)}
        title={`Monthly Revenue after OTA (${currency})`}
        monthTotalAccessor={regionMonthly ? (mi) => {
          const m = regionMonthly[mi];
          return currency === 'EUR' ? (m?.revenue_eur ?? null) : (m?.revenue_gbp ?? null);
        } : undefined}
        monthTotalFormatter={(v) => formatCompact(v, currency)}
        ytdAccessor={(p) => p.revenue_native}
        ytdTotal={totals.revenue}
      />

      {/* Gross Margin heatmap: per property (overheads are company-level, A148) */}
      <PropertyMonthHeatmap
        properties={propertiesInRegion}
        valueAccessor={(p, mi) => p.monthly[mi]?.gross_margin ?? null}
        colorScale="amber"
        formatter={(v) => formatCompact(v, currency)}
        title={`Monthly Gross Margin (${currency})`}
        monthTotalAccessor={regionMonthly ? (mi) => {
          const m = regionMonthly[mi];
          return currency === 'EUR' ? (m?.gross_margin_eur ?? null) : (m?.gross_margin_gbp ?? null);
        } : undefined}
        monthTotalFormatter={(v) => formatCompact(v, currency)}
        ytdAccessor={(p) => p.gross_margin_native}
        ytdTotal={totals.gross_margin}
      />

      {/* Occupancy heatmap */}
      <PropertyMonthHeatmap
        properties={propertiesInRegion}
        valueAccessor={(p, mi) => {
          const occ = p.monthly[mi]?.occupancy;
          return occ != null ? occ : null;
        }}
        colorScale="emerald"
        formatter={(v) => `${Math.round(v)}%`}
        title="Occupancy by Property"
        monthTotalAccessor={regionMonthly ? (mi) => {
          if (forecastMonthStart != null && mi >= forecastMonthStart && mi <= forecastMonthEnd) {
            const mk = `${year}-${String(mi + 1).padStart(2, '0')}`;
            const fm = forecastByMonth?.[mk];
            if (fm) return fm[forecastOccKey] ?? null;
            return null;
          }
          const m = regionMonthly[mi];
          return m?.occupancy_pct ?? null;
        } : undefined}
        monthTotalFormatter={(v) => `${Math.round(v)}%`}
        ytdAccessor={(p) => p.occupancy_ytd_pct}
        ytdTotal={totals.occupancy}
        forecastMonthStart={forecastMonthStart}
        forecastMonthEnd={forecastMonthEnd}
      />

      {/* Avg Net Nightly Rate heatmap */}
      <PropertyMonthHeatmap
        properties={propertiesInRegion}
        valueAccessor={(p, mi) => {
          const rate = p.monthly[mi]?.avg_nightly_rate;
          return rate > 0 ? rate : null;
        }}
        colorScale="blue"
        formatter={(v) => `${sym}${Math.round(v)}`}
        title={`Avg Net Nightly Rate by Property (${currency})`}
        monthTotalAccessor={regionMonthly ? (mi) => {
          if (forecastMonthStart != null && mi >= forecastMonthStart && mi <= forecastMonthEnd) {
            const mk = `${year}-${String(mi + 1).padStart(2, '0')}`;
            const fm = forecastByMonth?.[mk];
            if (fm) return toDisplayCurrency(fm[forecastAdrKey]);
            return null;
          }
          const m = regionMonthly[mi];
          const rate = m?.[nightlyRateKey];
          return rate > 0 ? rate : null;
        } : undefined}
        monthTotalFormatter={(v) => `${sym}${Math.round(v)}`}
        forecastMonthStart={forecastMonthStart}
        forecastMonthEnd={forecastMonthEnd}
        ytdAccessor={(p) => p.nights > 0 ? p.avg_nightly_rate_native : null}
        ytdTotal={nightlyRateYtdTotal}
      />

      {/* Channel breakdown */}
      {channels.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Channel Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-64 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={channels} dataKey="revenue" nameKey="channel" cx="50%" cy="50%" outerRadius={100}
                      label={({ channel, pct }) => `${channel} ${pct.toFixed(0)}%`} labelLine={false} fontSize={11}>
                      {channels.map((_, idx) => (
                        <Cell key={idx} fill={CHANNEL_COLORS[idx % CHANNEL_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => formatCurrency(v, currency)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {channels.map((ch, idx) => (
                  <div key={ch.channel} className="flex items-center gap-3 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[idx % CHANNEL_COLORS.length] }} />
                    <span className="flex-1 font-medium">{ch.channel}</span>
                    <span className="font-mono">{formatCurrency(ch.revenue, currency)}</span>
                    <span className="text-muted-foreground w-12 text-right">{ch.pct.toFixed(1)}%</span>
                    <span className="text-muted-foreground w-16 text-right">{ch.bookings} bk</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Country Properties Sub-tab ──────────────────────────────────────────────

function CountryPropertiesContent({ region, derived, displayCurrency, missingCostsMap = {} }) {
  const currency = displayCurrency;
  const properties = useMemo(
    () => derived.propertiesYtd.filter(p => p.region === region),
    [derived.propertiesYtd, region]
  );

  const [sortKey, setSortKey] = useState('net_revenue_native');
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const sorted = useMemo(() => {
    const arr = [...properties];
    arr.sort((a, b) => {
      if (sortKey === 'name') {
        const cmp = a.name.localeCompare(b.name);
        return sortAsc ? cmp : -cmp;
      }
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return sortAsc ? av - bv : bv - av;
    });
    return arr;
  }, [properties, sortKey, sortAsc]);

  const sortIcon = (key) => sortKey === key ? (sortAsc ? ' \u2191' : ' \u2193') : '';

  const hasAnyUnclassified = properties.some(p => (p.unclassified_native || 0) !== 0);

  const cols = [
    { key: 'name', label: 'Property', align: '' },
    { key: 'gross_revenue_native', label: 'Revenue', align: 'text-right' },
    { key: 'ota_commission_native', label: COST_BUCKET_LABELS.ota_commission, align: 'text-right' },
    // A148 tiers: Net Revenue after OTA, management fees, cleaning, repairs;
    // Gross Margin after the fixed property costs. Overheads are company-level,
    // so a property's last column is its Gross Margin.
    { key: 'management_fees_native', label: COST_BUCKET_LABELS.management_fees, align: 'text-right' },
    { key: 'cleaning_native', label: 'Cleaning', align: 'text-right' },
    { key: 'repairs_native', label: 'Repairs', align: 'text-right' },
    { key: 'net_revenue_native', label: PNL_SUBTOTAL_LABELS.net_revenue, align: 'text-right', subtotal: true },
    { key: 'financing_native', label: COST_BUCKET_LABELS.financing, align: 'text-right' },
    { key: 'other_operational_native', label: 'Other', align: 'text-right' },
    ...(hasAnyUnclassified ? [{ key: 'unclassified_native', label: COST_BUCKET_LABELS.unclassified, align: 'text-right text-amber-600' }] : []),
    { key: 'net_margin_native', label: 'Gross Margin', align: 'text-right' },
    { key: 'margin_pct', label: 'Gross Margin %', align: 'text-right' },
    { key: 'occupancy_ytd_pct', label: 'Occupancy', align: 'text-right' },
    { key: 'avg_nightly_rate_native', label: 'Avg Net Nightly Rate', align: 'text-right', tooltip: 'Avg Net Nightly Rate = net stay revenue / nights sold (net = gross minus OTA commission)' },
    { key: 'revpar_native', label: 'Net RevPAR', align: 'text-right', tooltip: 'Net RevPAR = net stay revenue / available nights' },
    { key: 'bookings_count', label: 'Bookings', align: 'text-right' },
    { key: 'nights', label: 'Nights', align: 'text-right' },
    { key: 'investment_native', label: 'Investment', align: 'text-right' },
    { key: 'roi_pct', label: 'ROI', align: 'text-right' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Properties ({properties.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                {cols.map(c => (
                  <TableHead
                    key={c.key}
                    className={cn('cursor-pointer select-none whitespace-nowrap', c.align)}
                    onClick={() => handleSort(c.key)}
                  >
                    {c.label}{sortIcon(c.key)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(prop => {
                const isExpanded = expandedId === prop.property_id;
                return (
                  <React.Fragment key={prop.property_id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedId(isExpanded ? null : prop.property_id)}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="font-medium whitespace-nowrap" title={prop.name}>
                        {shortPropertyName(prop.name)}
                        {(missingCostsMap[prop.property_id] ?? 0) > 0 && (
                          <a
                            href={`/costs?focus=${prop.property_id}`}
                            onClick={e => e.stopPropagation()}
                            title={`${missingCostsMap[prop.property_id]} cost types not yet entered`}
                          >
                            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950">
                              {missingCostsMap[prop.property_id]} gaps
                            </Badge>
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.gross_revenue_native, currency)}</TableCell>
                      <TableCell className="text-right font-mono text-red-500">{prop.ota_commission_native > 0 ? `-${formatCurrency(prop.ota_commission_native, currency)}` : '\u2014'}</TableCell>
                      <TableCell className="text-right font-mono font-semibold border-l-2 border-border/60 text-green-600 dark:text-green-400">{formatCurrency(prop.net_revenue_native, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.management_fees_native, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.cleaning_native || 0, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.repairs_native || 0, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.other_operational_native || 0, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.financing_native || 0, currency)}</TableCell>
                      {hasAnyUnclassified && (
                        <TableCell className="text-right font-mono text-amber-600">{formatCurrency(prop.unclassified_native || 0, currency)}</TableCell>
                      )}
                      <TableCell className={cn('text-right font-mono', prop.net_margin_native >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                        {formatCurrency(prop.net_margin_native, currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{prop.margin_pct.toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">{prop.occupancy_ytd_pct.toFixed(0)}%</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.avg_nightly_rate_native, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(prop.revpar_native || 0, currency)}</TableCell>
                      <TableCell className="text-right font-mono">{prop.bookings_count}</TableCell>
                      <TableCell className="text-right font-mono">{prop.nights}</TableCell>
                      <TableCell className="text-right font-mono">
                        {prop.investment_native != null
                          ? formatCurrency(prop.investment_native, prop.investment_currency || currency)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {prop.roi_pct != null
                          ? `${prop.roi_pct.toFixed(1)}%`
                          : (
                            <span className="text-muted-foreground" title="Add investment cost to calculate ROI">
                              {'- '}<a href={`/costs?focus=${prop.property_id}`} className="text-xs text-blue-500 hover:underline ml-1" onClick={e => e.stopPropagation()}>Add</a>
                            </span>
                          )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded: 12-month breakdown */}
                    {isExpanded && (
                      <TableRow className="bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={cols.length}>
                          <div className="py-3 space-y-4">
                            <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Monthly Breakdown</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b text-muted-foreground">
                                    <th className="text-left py-1 pr-3">Month</th>
                                    <th className="text-right py-1 pr-3">Revenue</th>
                                    <th className="text-right py-1 pr-3">{COST_BUCKET_LABELS_SHORT.ota_commission}</th>
                                    <th className="text-right py-1 pr-3 font-semibold">{PNL_SUBTOTAL_LABELS_SHORT.net_revenue}</th>
                                    <th className="text-right py-1 pr-3">{COST_BUCKET_LABELS_SHORT.management_fees}</th>
                                    <th className="text-right py-1 pr-3">Cleaning</th>
                                    <th className="text-right py-1 pr-3">Repairs</th>
                                    <th className="text-right py-1 pr-3">Other</th>
                                    <th className="text-right py-1 pr-3">{COST_BUCKET_LABELS_SHORT.financing}</th>
                                    <th className="text-right py-1 pr-3">{PNL_SUBTOTAL_LABELS_SHORT.net_profit}</th>
                                    <th className="text-right py-1 pr-3">Margin %</th>
                                    <th className="text-right py-1 pr-3">Occupancy</th>
                                    <th className="text-right py-1 pr-3">Avg Net Nightly Rate</th>
                                    <th className="text-right py-1 pr-3">Net RevPAR</th>
                                    <th className="text-right py-1 pr-3">Bookings</th>
                                    <th className="text-right py-1">Nights</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {prop.monthly.map(m => {
                                    const beforeListed = prop.first_listed_date && m.month < prop.first_listed_date.substring(0, 7);
                                    if (beforeListed) {
                                      return (
                                        <tr key={m.month} className="border-b border-border/50">
                                          <td className="py-1 pr-3 font-medium">{m.label}</td>
                                          <td colSpan={15} className="text-center text-muted-foreground py-1">-</td>
                                        </tr>
                                      );
                                    }
                                    return (
                                      <tr key={m.month} className="border-b border-border/50">
                                        <td className="py-1 pr-3 font-medium">{m.label}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.gross_revenue, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono text-red-500">{m.ota_commission > 0 ? `-${formatCurrency(m.ota_commission, currency)}` : '\u2014'}</td>
                                        <td className="text-right py-1 pr-3 font-mono font-semibold">{formatCurrency(m.net_revenue, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.management_fees, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.cleaning || 0, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.repairs || 0, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.other_operational || 0, currency)}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{formatCurrency(m.financing || 0, currency)}</td>
                                        <td className={cn('text-right py-1 pr-3 font-mono', m.net_margin >= 0 ? 'text-green-600' : 'text-red-600')}>
                                          {formatCurrency(m.net_margin, currency)}
                                        </td>
                                        <td className="text-right py-1 pr-3 font-mono">{m.net_revenue > 0 ? `${m.margin_pct.toFixed(1)}%` : '-'}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{`${m.occupancy.toFixed(0)}%`}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{m.nights > 0 ? formatCurrency(m.adr || m.avg_nightly_rate, currency) : '-'}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{m.available_nights > 0 ? formatCurrency(m.revpar || 0, currency) : '-'}</td>
                                        <td className="text-right py-1 pr-3 font-mono">{m.bookings}</td>
                                        <td className="text-right py-1 font-mono">{m.nights}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols.length + 1} className="text-center py-8 text-muted-foreground">
                    No properties in this region.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Country Bookings Sub-tab ────────────────────────────────────────────────
// Expandable monthly groups: collapsed month header rows with booking details
// revealed on click. Follows the same pattern as RevenuePage entity-mode.

function CountryBookingsContent({ region, derived, displayCurrency, year, selectedProperties, setSelectedProperties, selectedChannels, setSelectedChannels, monthlyGroups, totals }) {
  // Booking-level amounts are always in the region's native currency (not converted)
  const currency = region === 'uk' ? 'GBP' : 'EUR';
  const fmt = (v) => formatCurrency(v, currency);

  const allBookings = useMemo(
    () => derived.bookings.filter(b => b.region === region),
    [derived.bookings, region]
  );

  const [expandedMonths, setExpandedMonths] = useState(new Set());

  const toggleMulti = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  // Unique values for filters
  const propertyOptions = useMemo(() => {
    const names = new Set();
    allBookings.forEach(b => names.add(b.property_name));
    return [...names].sort();
  }, [allBookings]);

  const channelOptions = useMemo(() => {
    const chs = new Set();
    allBookings.forEach(b => chs.add(b.channel || 'other'));
    return [...chs].sort();
  }, [allBookings]);

  const isCurrentYear = year === new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" /> Filters:
            </div>

            {/* Property filter */}
            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={e => { if (e.target.value) toggleMulti(selectedProperties, setSelectedProperties, e.target.value); }}
            >
              <option value="">Property ({selectedProperties.length || 'All'})</option>
              {propertyOptions.map(p => (
                <option key={p} value={p}>{selectedProperties.includes(p) ? '\u2713 ' : ''}{p}</option>
              ))}
            </select>

            {/* Channel filter */}
            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={e => { if (e.target.value) toggleMulti(selectedChannels, setSelectedChannels, e.target.value); }}
            >
              <option value="">Channel ({selectedChannels.length || 'All'})</option>
              {channelOptions.map(c => (
                <option key={c} value={c}>{selectedChannels.includes(c) ? '\u2713 ' : ''}{c}</option>
              ))}
            </select>

            {/* Active filter badges */}
            {(selectedProperties.length > 0 || selectedChannels.length > 0) && (
              <>
                {selectedProperties.map(p => (
                  <Badge key={p} variant="secondary" className="text-xs cursor-pointer" onClick={() => toggleMulti(selectedProperties, setSelectedProperties, p)}>
                    {p} x
                  </Badge>
                ))}
                {selectedChannels.map(c => (
                  <Badge key={c} variant="secondary" className="text-xs cursor-pointer" onClick={() => toggleMulti(selectedChannels, setSelectedChannels, c)}>
                    {c} x
                  </Badge>
                ))}
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => { setSelectedProperties([]); setSelectedChannels([]); }}>
                  Clear all
                </Button>
              </>
            )}

            <span className="text-xs text-muted-foreground ml-auto">{totals.count} bookings</span>
          </div>
        </CardContent>
      </Card>

      {/* Monthly expandable table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Booking Volume</TableHead>
                  <TableHead className="text-right">{COST_BUCKET_LABELS.ota_commission}</TableHead>
                  <TableHead className="text-right">{PNL_SUBTOTAL_LABELS.net_revenue}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyGroups
                  .filter(g => {
                    // Show months up to current for current year, all for past years
                    if (year < new Date().getFullYear()) return true;
                    if (year > new Date().getFullYear()) return false;
                    return g.month <= currentMonth;
                  })
                  .map(g => {
                    const hasBookings = g.count > 0;
                    const isExpanded = expandedMonths.has(g.month);
                    const isCurrent = isCurrentYear && g.month === currentMonth;
                    const toggleExpand = () => {
                      setExpandedMonths(prev => {
                        const next = new Set(prev);
                        next.has(g.month) ? next.delete(g.month) : next.add(g.month);
                        return next;
                      });
                    };

                    return (
                      <React.Fragment key={g.month}>
                        {/* Month header row */}
                        <TableRow
                          className={cn(
                            isCurrent && 'bg-primary/5',
                            hasBookings && 'cursor-pointer hover:bg-muted/50'
                          )}
                          onClick={hasBookings ? toggleExpand : undefined}
                        >
                          <TableCell className="w-8 px-2">
                            {hasBookings && (isExpanded
                              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {g.label}
                            {isCurrent && (
                              <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                                Now
                              </Badge>
                            )}
                            {hasBookings && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {g.count} booking{g.count !== 1 ? 's' : ''}, {g.nights} night{g.nights !== 1 ? 's' : ''}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {hasBookings ? fmt(g.gross) : '\u2014'}
                            {hasBookings && g.unavailableGuestTotalCount > 0 && (
                              <span
                                className="ml-1 text-amber-600 dark:text-amber-500"
                                title={`${g.unavailableGuestTotalCount} booking(s) this month have no guest total available from the platform -- excluded from this figure, not shown as zero`}
                              >
                                *
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-500">
                            {hasBookings && g.otaCommission > 0 ? `-${fmt(g.otaCommission)}` : '\u2014'}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {hasBookings ? fmt(g.netRevenue) : '\u2014'}
                          </TableCell>
                        </TableRow>

                        {/* Expanded booking detail rows */}
                        {isExpanded && g.bookings.map((b, i) => (
                          <TableRow key={`${g.month}-${i}`} className="bg-muted/20 text-xs">
                            <TableCell></TableCell>
                            <TableCell className="py-1.5">
                              <span className="font-medium">{b.property_name}</span>
                              <span className="text-muted-foreground ml-2">{b.guest_name}</span>
                              <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{b.channel}</Badge>
                              <span className="text-muted-foreground ml-2">{b.check_in} &rarr; {b.check_out}</span>
                              <span className="text-muted-foreground ml-1">({b.nights}n)</span>
                              {b.invoice_status === 'invoiced' && (
                                <Badge className="ml-2 text-[10px] px-1 py-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0">
                                  Invoiced{b.invoice_number ? ` ${b.invoice_number}` : ''}
                                </Badge>
                              )}
                              {b.invoice_status === 'draft' && (
                                <Badge className="ml-2 text-[10px] px-1 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Draft</Badge>
                              )}
                              {b.invoice_status === 'not_invoiced' && (
                                <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 text-muted-foreground">Not invoiced</Badge>
                              )}
                              {b.invoice_status === 'cancelled' && (
                                <Badge className="ml-2 text-[10px] px-1 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">Cancelled</Badge>
                              )}
                              {b.mgmt_statement_status === 'reconciled' && (
                                <Badge className="ml-2 text-[10px] px-1 py-0 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0">Reconciled with mgmt statement</Badge>
                              )}
                              {b.mgmt_statement_status === 'not_reconciled' && (
                                <Badge className="ml-2 text-[10px] px-1 py-0 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-0">Not on statement</Badge>
                              )}
                              {b.mgmt_statement_status === 'ref_matched_amount_mismatch' && (
                                <Badge
                                  className="ml-2 text-[10px] px-1 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0"
                                  title={
                                    b.display_gross != null && b.statement_gross != null
                                      ? `Platform ${fmt(b.display_gross)} vs statement ${fmt(b.statement_gross)} -- differs by more than the reconciliation tolerance`
                                      : 'Platform and management-statement gross differ by more than the reconciliation tolerance'
                                  }
                                >
                                  Statement amount mismatch
                                </Badge>
                              )}
                              {b.mgmt_statement_status != null
                                && !['reconciled', 'not_reconciled', 'ref_matched_amount_mismatch'].includes(b.mgmt_statement_status) && (
                                <Badge
                                  variant="destructive"
                                  className="ml-2 text-[10px] px-1 py-0"
                                  title="The reconciliation resolver returned a status this UI has no label for -- surfacing the raw value rather than rendering no badge at all."
                                >
                                  {b.mgmt_statement_status}
                                </Badge>
                              )}
                              {b.paid_badge && (
                                <span
                                  className="ml-2"
                                  title={`Invoice ${b.paid_badge.invoice_number || 'n/a'}${b.paid_badge.reconciled_at ? ` · reconciled ${b.paid_badge.reconciled_at}` : ''}`}
                                >
                                  <PayoutStateBadge state="Paid" />
                                </span>
                              )}
                              {b.statement_paid_badge && (
                                <span
                                  className="ml-2"
                                  title={`Statement ${b.statement_paid_badge.statement_id} (${b.statement_paid_badge.period || 'n/a'})${b.statement_paid_badge.paid_at ? ` · paid ${b.statement_paid_badge.paid_at}` : ''}`}
                                >
                                  <PayoutStateBadge state="Paid" />
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5">
                              {b.display_gross != null ? fmt(b.display_gross) : (
                                <span className="text-amber-600 dark:text-amber-500 italic" title="The platform (Hostaway/Smoobu) has no guest total for this booking -- never silently substituted with the management statement or the fee-basis figure.">
                                  guest total unavailable
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5 text-red-500">
                              {b.display_gross == null ? '\u2014' : ((b.display_commission || 0) > 0 ? `-${fmt(b.display_commission)}` : '\u2014')}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5">
                              {b.display_net != null ? fmt(b.display_net) : '\u2014'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                {/* Total row */}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell></TableCell>
                  <TableCell>Total ({totals.count} bookings, {totals.nights} nights)</TableCell>
                  <TableCell className="text-right font-mono">{fmt(totals.gross)}</TableCell>
                  <TableCell className="text-right font-mono text-red-500">
                    {totals.otaCommission > 0 ? `-${fmt(totals.otaCommission)}` : '\u2014'}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmt(totals.netRevenue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── PDF Export ───────────────────────────────────────────────────────────────

function slugify(str) {
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

async function exportPDF({ derived, year, topTab, countrySubTab, filters, bookingsData, displayCurrency }) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const generatedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const PRIMARY = [22, 101, 52];
  const MUTED = [100, 116, 139];
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 14;

  const dc = displayCurrency || 'GBP';
  const fmtPdf = (v, cur) => {
    const sym = currencySymbol(cur);
    return `${v < 0 ? '-' : ''}${sym}${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Determine title, scope, filename ──
  const countryLabel = topTab === 'fr' ? 'France' : 'UK';
  // Filters are only meaningful on the Bookings sub-tab
  const hasFilters = countrySubTab === 'bookings' && (filters.properties.length > 0 || filters.channels.length > 0);
  const filterParts = [];
  if (hasFilters && filters.properties.length > 0) filterParts.push(filters.properties.join(', '));
  if (hasFilters && filters.channels.length > 0) filterParts.push(filters.channels.join(', '));
  const filtersLine = hasFilters ? `Filtered by: ${filterParts.join(' / ')}` : null;

  const fileRegion = topTab === 'fr' ? 'france' : 'uk';
  const curSuffix = dc === 'EUR' ? '-eur' : '';

  let scopeLine, fileName;
  if (topTab === 'all') {
    scopeLine = `All Markets \u00B7 ${year} \u00B7 ${dc}`;
    fileName = `portfolio-all-${year}${curSuffix}.pdf`;
  } else if (countrySubTab === 'overview') {
    scopeLine = `${countryLabel} \u00B7 Overview \u00B7 ${year}`;
    fileName = `portfolio-${fileRegion}-overview-${year}${curSuffix}.pdf`;
  } else if (countrySubTab === 'properties') {
    scopeLine = `${countryLabel} \u00B7 Properties \u00B7 ${year}`;
    fileName = `portfolio-${fileRegion}-properties-${year}${curSuffix}.pdf`;
  } else {
    scopeLine = `${countryLabel} \u00B7 Bookings \u00B7 ${year}`;
    const propSlug = filters.properties.length > 0 ? `-${slugify(filters.properties[0])}` : '';
    fileName = `portfolio-${fileRegion}-bookings-${year}${propSlug}${curSuffix}.pdf`;
  }

  // ── Header drawing helper ──
  let headerY = 0;
  const drawHeader = () => {
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, PAGE_W, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bonjour Residences \u2014 Portfolio Report', MARGIN, 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(scopeLine, MARGIN, 17);
    if (filtersLine) {
      doc.setFontSize(8);
      doc.text(filtersLine, MARGIN, 23);
    }
    doc.setFontSize(8);
    doc.text(`Generated ${generatedOn}`, PAGE_W - MARGIN, 10, { align: 'right' });
    headerY = filtersLine ? 32 : 30;
  };

  drawHeader();

  // ── Content by context ──

  if (topTab === 'all') {
    // All tab: country-level KPI summary, monthly revenue table, YTD occupancy + nightly rate
    const t = derived.totals.combined;
    const uk = derived.totals.uk;
    const fr = derived.totals.fr;

    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('KPI Summary', MARGIN, headerY + 4);

    autoTable(doc, {
      startY: headerY + 7,
      margin: { left: MARGIN, right: MARGIN },
      head: [['', 'Revenue after OTA', 'Net Margin', 'Margin %', 'Occupancy', 'Avg Net Nightly Rate', 'Bookings', 'Nights']],
      body: [
        [`Combined (${dc})`, fmtPdf(t.revenue_gbp, dc), fmtPdf(t.net_margin_gbp, dc), `${t.margin_pct.toFixed(1)}%`, `${t.occupancy.toFixed(0)}%`, fmtPdf(t.avg_nightly_rate_gbp, dc), String(t.bookings), String(t.nights)],
        [`UK (${dc})`, fmtPdf(uk.revenue, dc), fmtPdf(uk.net_margin, dc), `${uk.margin_pct.toFixed(1)}%`, `${uk.occupancy.toFixed(0)}%`, fmtPdf(uk.avg_nightly_rate, dc), String(uk.bookings), String(uk.nights)],
        [`France (${dc})`, fmtPdf(fr.revenue, dc), fmtPdf(fr.net_margin, dc), `${fr.margin_pct.toFixed(1)}%`, `${fr.occupancy.toFixed(0)}%`, fmtPdf(fr.avg_nightly_rate, dc), String(fr.bookings), String(fr.nights)],
      ],
      headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { fontStyle: 'bold' } },
      theme: 'grid',
    });

    // Monthly revenue table for each region
    for (const rk of ['uk', 'fr']) {
      const cur = dc;
      const monthly = derived.monthlyByRegion[rk];
      const revKey = dc === 'EUR' ? 'revenue_eur' : 'revenue_gbp';
      const nmKey = dc === 'EUR' ? 'net_margin_eur' : 'net_margin_gbp';

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(`${rk === 'uk' ? 'UK' : 'France'} (${dc}) Monthly`, MARGIN, doc.lastAutoTable.finalY + 10);

      const monthRows = monthly.map(m => [
        m.label,
        fmtPdf(m[revKey] || 0, cur),
        fmtPdf(m[nmKey] || 0, cur),
        `${Math.round(m.occupancy_pct)}%`,
        fmtPdf(m[dc === 'EUR' ? 'avg_nightly_rate_eur' : 'avg_nightly_rate_gbp'] || 0, cur),
        String(m.bookings),
        String(m.nights),
      ]);

      // YTD row
      const regTotals = rk === 'uk' ? uk : fr;
      const ytdRev = monthly.reduce((s, m) => s + (m[revKey] || 0), 0);
      const ytdNm = monthly.reduce((s, m) => s + (m[nmKey] || 0), 0);
      const ytdBk = monthly.reduce((s, m) => s + m.bookings, 0);
      const ytdNights = monthly.reduce((s, m) => s + m.nights, 0);
      monthRows.push(['YTD', fmtPdf(ytdRev, cur), fmtPdf(ytdNm, cur), `${regTotals.occupancy.toFixed(0)}%`, fmtPdf(regTotals.avg_nightly_rate, cur), String(ytdBk), String(ytdNights)]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 13,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Month', `Revenue after OTA (${cur})`, 'Net Margin', 'Occupancy', 'Avg/Night', 'Bookings', 'Nights']],
        body: monthRows,
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { fontStyle: 'bold' } },
        theme: 'grid',
      });
    }

  } else if (countrySubTab === 'overview') {
    // Overview sub-tab: per-property heatmap as table (Revenue after OTA, Net Margin, Occupancy, Avg Net Nightly Rate)
    const cur = dc;
    const props = derived.propertiesYtd
      .filter(p => p.region === topTab)
      .sort((a, b) => b.revenue_native - a.revenue_native);

    const heatmapSection = (sectionTitle, valueExtractor, formatter, ytdExtractor) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      const yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : headerY + 4;

      // Check page break
      if (yPos > doc.internal.pageSize.getHeight() - 50) {
        doc.addPage();
        drawHeader();
      }
      const actualY = doc.lastAutoTable && yPos <= doc.internal.pageSize.getHeight() - 50 ? yPos : headerY + 4;
      doc.text(sectionTitle, MARGIN, actualY);

      const head = [['Property', ...MONTHS, 'YTD']];
      const body = props.map(p => {
        const row = [p.name];
        for (let mi = 0; mi < 12; mi++) {
          const v = valueExtractor(p, mi);
          row.push(v != null ? formatter(v) : '-');
        }
        const ytd = ytdExtractor(p);
        row.push(ytd != null ? formatter(ytd) : '-');
        return row;
      });

      autoTable(doc, {
        startY: actualY + 3,
        margin: { left: MARGIN, right: MARGIN },
        head,
        body,
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 6, cellPadding: 1.5 },
        bodyStyles: { fontSize: 6, cellPadding: 1.5 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30 } },
        theme: 'grid',
      });
    };

    heatmapSection(
      `Revenue after OTA (${cur})`,
      (p, mi) => p.monthly[mi]?.revenue ?? null,
      (v) => fmtPdf(v, cur),
      (p) => p.revenue_native
    );

    heatmapSection(
      `Gross Margin (${cur})`,
      (p, mi) => p.monthly[mi]?.gross_margin ?? null,
      (v) => fmtPdf(v, cur),
      (p) => p.gross_margin_native
    );

    heatmapSection(
      'Occupancy %',
      (p, mi) => { const o = p.monthly[mi]?.occupancy; return o != null ? o : null; },
      (v) => `${Math.round(v)}%`,
      (p) => p.occupancy_ytd_pct
    );

    heatmapSection(
      `Avg Net Nightly Rate (${cur})`,
      (p, mi) => { const r = p.monthly[mi]?.avg_nightly_rate; return r > 0 ? r : null; },
      (v) => fmtPdf(v, cur),
      (p) => p.nights > 0 ? p.avg_nightly_rate_native : null
    );

  } else if (countrySubTab === 'properties') {
    // Properties sub-tab: YTD per-property table with monthly drill-down
    const cur = dc;
    const props = derived.propertiesYtd
      .filter(p => p.region === topTab)
      .sort((a, b) => b.net_revenue_native - a.net_revenue_native);

    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Properties YTD (${props.length})`, MARGIN, headerY + 4);

    const propRows = props.map(p => [
      p.name,
      fmtPdf(p.gross_revenue_native, cur),
      p.ota_commission_native > 0 ? fmtPdf(-p.ota_commission_native, cur) : '-',
      fmtPdf(p.net_revenue_native, cur),
      fmtPdf(p.management_fees_native, cur),
      fmtPdf(p.cleaning_native || 0, cur),
      fmtPdf(p.repairs_native || 0, cur),
      fmtPdf(p.other_operational_native || 0, cur),
      fmtPdf(p.financing_native || 0, cur),
      fmtPdf(p.net_margin_native, cur),
      `${p.margin_pct.toFixed(1)}%`,
      `${p.occupancy_ytd_pct.toFixed(0)}%`,
      fmtPdf(p.avg_nightly_rate_native, cur),
      String(p.bookings_count),
      String(p.nights),
      p.investment_native != null ? fmtPdf(p.investment_native, p.investment_currency || cur) : '-',
      p.roi_pct != null ? `${p.roi_pct.toFixed(1)}%` : '-',
    ]);

    autoTable(doc, {
      startY: headerY + 7,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Property', 'Revenue', COST_BUCKET_LABELS_SHORT.ota_commission, PNL_SUBTOTAL_LABELS_SHORT.net_revenue, COST_BUCKET_LABELS_SHORT.management_fees, 'Cleaning', 'Repairs', 'Other', COST_BUCKET_LABELS_SHORT.financing, PNL_SUBTOTAL_LABELS_SHORT.net_profit, 'Margin %', 'Occ.', 'Avg/Night', 'Bkgs', 'Nights', 'Investment', 'ROI']],
      body: propRows,
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      bodyStyles: { fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { fontStyle: 'bold' } },
      theme: 'grid',
    });

    // Monthly drill-down per property
    for (const p of props) {
      let needsPageBreak = doc.lastAutoTable.finalY > doc.internal.pageSize.getHeight() - 40;
      if (needsPageBreak) {
        doc.addPage();
        drawHeader();
      }

      const labelY = needsPageBreak ? headerY + 4 : doc.lastAutoTable.finalY + 8;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(p.name, MARGIN, labelY);

      const monthRows = p.monthly.map(m => {
        const beforeListed = p.first_listed_date && m.month < p.first_listed_date.substring(0, 7);
        if (beforeListed) return [m.label, '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-'];
        return [
          m.label,
          fmtPdf(m.gross_revenue, cur),
          m.ota_commission > 0 ? fmtPdf(-m.ota_commission, cur) : '-',
          fmtPdf(m.net_revenue, cur),
          fmtPdf(m.management_fees, cur),
          fmtPdf(m.cleaning || 0, cur),
          fmtPdf(m.repairs || 0, cur),
          fmtPdf(m.other_operational || 0, cur),
          fmtPdf(m.financing || 0, cur),
          fmtPdf(m.net_margin, cur),
          m.net_revenue > 0 ? `${m.margin_pct.toFixed(1)}%` : '-',
          `${m.occupancy.toFixed(0)}%`,
          m.nights > 0 ? fmtPdf(m.avg_nightly_rate, cur) : '-',
          String(m.nights),
        ];
      });

      autoTable(doc, {
        startY: labelY + 3,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Month', 'Revenue', COST_BUCKET_LABELS_SHORT.ota_commission, PNL_SUBTOTAL_LABELS_SHORT.net_revenue, COST_BUCKET_LABELS_SHORT.management_fees, 'Cleaning', 'Repairs', 'Other', COST_BUCKET_LABELS_SHORT.financing, PNL_SUBTOTAL_LABELS_SHORT.net_profit, 'Margin %', 'Occ.', 'Avg/Night', 'Nights']],
        body: monthRows,
        headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold', fontSize: 6, cellPadding: 1.2 },
        bodyStyles: { fontSize: 6, cellPadding: 1.2 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { fontStyle: 'bold' } },
        theme: 'grid',
      });
    }

  } else if (countrySubTab === 'bookings') {
    // Bookings sub-tab: booking amounts are always in region's native currency
    const cur = topTab === 'uk' ? 'GBP' : 'EUR';
    const { monthlyGroups, totals } = bookingsData || { monthlyGroups: [], totals: { count: 0, nights: 0, gross: 0, otaCommission: 0, netRevenue: 0 } };

    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Bookings (${totals.count} total, ${totals.nights} nights)`, MARGIN, headerY + 4);

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    for (const g of monthlyGroups) {
      if (year === currentYear && g.month > currentMonth) continue;
      if (g.count === 0) continue;

      let bkPageBreak = doc.lastAutoTable && doc.lastAutoTable.finalY > doc.internal.pageSize.getHeight() - 40;
      if (bkPageBreak) {
        doc.addPage();
        drawHeader();
      }

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      const yPos = bkPageBreak ? headerY + 8 : (doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerY + 8);
      doc.text(`${g.label} (${g.count} bookings, ${g.nights} nights)`, MARGIN, yPos);

      const rows = g.bookings.map(b => [
        b.property_name,
        b.guest_name || '-',
        b.channel || '-',
        `${b.check_in} - ${b.check_out}`,
        String(b.nights),
        b.display_gross != null ? fmtPdf(b.display_gross, cur) : 'unavailable',
        b.display_gross == null ? '-' : ((b.display_commission || 0) > 0 ? fmtPdf(b.display_commission, cur) : '-'),
        b.display_net != null ? fmtPdf(b.display_net, cur) : '-',
      ]);

      // Month subtotal row
      rows.push([
        { content: `${g.label} Total`, colSpan: 4, styles: { fontStyle: 'bold' } },
        String(g.nights),
        fmtPdf(g.gross, cur),
        g.otaCommission > 0 ? fmtPdf(g.otaCommission, cur) : '-',
        fmtPdf(g.netRevenue, cur),
      ]);

      autoTable(doc, {
        startY: yPos + 2,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Property', 'Guest', 'Channel', 'Dates', 'Nts', 'Booking Volume', COST_BUCKET_LABELS.ota_commission, PNL_SUBTOTAL_LABELS.net_revenue]],
        body: rows,
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 6, cellPadding: 1.2 },
        bodyStyles: { fontSize: 6, cellPadding: 1.2 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        theme: 'grid',
      });
    }

    // Grand total
    if (doc.lastAutoTable) {
      let gtPageBreak = doc.lastAutoTable.finalY > doc.internal.pageSize.getHeight() - 20;
      if (gtPageBreak) {
        doc.addPage();
        drawHeader();
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      const totalY = gtPageBreak ? headerY + 4 : doc.lastAutoTable.finalY + 6;
      doc.text(
        `Grand Total: ${totals.count} bookings, ${totals.nights} nights | Gross: ${fmtPdf(totals.gross, cur)} | Net: ${fmtPdf(totals.netRevenue, cur)} | Margin: ${fmtPdf(totals.margin, cur)}`,
        MARGIN, totalY
      );
    }
  }

  // ── Footer on all pages ──
  const footerNote = topTab === 'all'
    ? `${dc} figures are approximate (today's FX rate). Includes recurring costs.`
    : 'Includes recurring costs.';
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text(footerNote, MARGIN, doc.internal.pageSize.getHeight() - 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}  \u00B7  Confidential \u2014 Bonjour Residences`,
      PAGE_W / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'center' }
    );
  }

  doc.save(fileName);
}

// ─── Excel Export ─────────────────────────────────────────────────────────────

async function exportExcel({ derived, year, topTab, countrySubTab, filters, bookingsData, displayCurrency }) {
  const XLSX = await import('xlsx');

  const wb = XLSX.utils.book_new();

  // ── File naming (mirrors PDF) ──
  const dc = displayCurrency || 'GBP';
  const fileRegion = topTab === 'fr' ? 'france' : 'uk';
  const curSuffix = dc === 'EUR' ? '-eur' : '';
  const hasFilters = countrySubTab === 'bookings' && (filters.properties.length > 0 || filters.channels.length > 0);
  let fileName;
  if (topTab === 'all') {
    fileName = `portfolio-all-${year}${curSuffix}.xlsx`;
  } else if (countrySubTab === 'overview') {
    fileName = `portfolio-${fileRegion}-overview-${year}${curSuffix}.xlsx`;
  } else if (countrySubTab === 'properties') {
    fileName = `portfolio-${fileRegion}-properties-${year}${curSuffix}.xlsx`;
  } else {
    const propSlug = filters.properties.length > 0 ? `-${slugify(filters.properties[0])}` : '';
    fileName = `portfolio-${fileRegion}-bookings-${year}${propSlug}${curSuffix}.xlsx`;
  }

  // ── Helper: append sheet with column widths and number formats ──
  const addSheet = (name, aoa, opts = {}) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Column widths from header/data lengths (min 10)
    if (aoa.length > 0) {
      ws['!cols'] = aoa[0].map((h, ci) => {
        let max = String(h || '').length;
        for (let ri = 1; ri < Math.min(aoa.length, 40); ri++) {
          const cell = aoa[ri]?.[ci];
          if (cell != null) max = Math.max(max, String(cell).length);
        }
        return { wch: Math.max(max + 2, 10) };
      });
    }

    // Apply number formats
    if (opts.formats) {
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let ci = range.s.c; ci <= range.e.c; ci++) {
        const fmt = opts.formats[ci];
        if (!fmt) continue;
        for (let ri = range.s.r + 1; ri <= range.e.r; ri++) {
          const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
          if (ws[addr] && typeof ws[addr].v === 'number') {
            ws[addr].z = fmt;
          }
        }
      }
    }

    if (opts.merges) ws['!merges'] = opts.merges;

    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  const ML = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const CUR_FMT = '#,##0.00';
  const PCT_FMT = '0.00%';

  // ── All tab ──
  if (topTab === 'all') {
    const t = derived.totals.combined;
    const uk = derived.totals.uk;
    const fr = derived.totals.fr;

    // Sheet 1: Summary
    // convertDerivedToDisplayCurrency already converts all region totals to dc,
    // so fr.revenue / fr.avg_nightly_rate are in display currency. No manual FX.
    const summaryRows = [
      ['', `Revenue after OTA (${dc})`, 'Occupancy %', `Avg Net Nightly Rate (${dc})`, 'Properties Count', 'Booked Nights'],
      [`Combined`, t.revenue_gbp, t.occupancy / 100, t.avg_nightly_rate_gbp, t.properties_count, t.nights],
      ['UK', uk.revenue, uk.occupancy / 100, uk.avg_nightly_rate, uk.properties_count, uk.nights],
      ['France', fr.revenue, fr.occupancy / 100, fr.avg_nightly_rate, fr.properties_count, fr.nights],
    ];
    addSheet('Summary', summaryRows, {
      formats: { 1: CUR_FMT, 2: PCT_FMT, 3: CUR_FMT },
    });

    // Sheet 2: Monthly Revenue after OTA
    const ukRevKey = dc === 'EUR' ? 'revenue_eur' : 'revenue_gbp';
    const frRevKey = 'revenue_eur';
    const monthlyRevRows = [['Month', `UK ${dc}`, 'FR EUR', `FR ${dc}${dc === 'EUR' ? '' : ' (approx)'}`, `Combined ${dc}`]];
    let ytdUk = 0, ytdFrE = 0, ytdFrC = 0, ytdAll = 0;
    for (let m = 0; m < 12; m++) {
      const ukRev = derived.monthlyByRegion.uk[m][ukRevKey] || 0;
      const frEur = derived.monthlyByRegion.fr[m][frRevKey] || 0;
      const frConverted = dc === 'EUR' ? frEur : frEur * derived.fx.EUR_GBP;
      const comb = ukRev + frConverted;
      ytdUk += ukRev; ytdFrE += frEur; ytdFrC += frConverted; ytdAll += comb;
      monthlyRevRows.push([ML[m], ukRev, frEur, frConverted, comb]);
    }
    monthlyRevRows.push(['YTD', ytdUk, ytdFrE, ytdFrC, ytdAll]);
    addSheet('Monthly Revenue after OTA', monthlyRevRows, {
      formats: { 1: CUR_FMT, 2: CUR_FMT, 3: CUR_FMT, 4: CUR_FMT },
    });

    // Sheet 3: Monthly Occupancy & Rate
    const rateKey = dc === 'EUR' ? 'avg_nightly_rate_eur' : 'avg_nightly_rate_gbp';
    const monthlyOccRows = [['Month', 'FR Occupancy', 'UK Occupancy', 'All Occupancy', `FR Net Nightly Rate (${dc})`, `UK Net Nightly Rate (${dc})`, `All Net Nightly Rate (${dc})`]];
    for (let m = 0; m < 12; m++) {
      const ukM = derived.monthlyByRegion.uk[m];
      const frM = derived.monthlyByRegion.fr[m];
      const allM = derived.monthlyCombined[m];
      monthlyOccRows.push([
        ML[m],
        (frM.occupancy_pct || 0) / 100,
        (ukM.occupancy_pct || 0) / 100,
        (allM.occupancy_pct || 0) / 100,
        frM[rateKey] || 0,
        ukM[rateKey] || 0,
        allM.avg_nightly_rate_gbp || 0,
      ]);
    }
    const ytdC = derived.ytdCombined;
    monthlyOccRows.push([
      'YTD',
      (ytdC.france.occupancy_pct || 0) / 100,
      (ytdC.uk.occupancy_pct || 0) / 100,
      (ytdC.combined.occupancy_pct || 0) / 100,
      derived.totals.fr.avg_nightly_rate || 0,
      derived.totals.uk.avg_nightly_rate || 0,
      derived.totals.combined.avg_nightly_rate_gbp || 0,
    ]);
    addSheet('Monthly Occupancy & Rate', monthlyOccRows, {
      formats: { 1: PCT_FMT, 2: PCT_FMT, 3: PCT_FMT, 4: CUR_FMT, 5: CUR_FMT, 6: CUR_FMT },
    });

  // ── Country tab: Overview ──
  } else if (countrySubTab === 'overview') {
    const cur = dc;
    const props = derived.propertiesYtd
      .filter(p => p.region === topTab)
      .sort((a, b) => b.revenue_native - a.revenue_native);

    const buildHeatmap = (name, valFn, ytdFn, numFmt) => {
      const rows = [['Property', ...ML, 'YTD']];
      for (const p of props) {
        const row = [p.name];
        for (let mi = 0; mi < 12; mi++) row.push(valFn(p, mi));
        row.push(ytdFn(p));
        rows.push(row);
      }
      const formats = {};
      for (let ci = 1; ci <= 13; ci++) formats[ci] = numFmt;
      addSheet(name, rows, { formats });
    };

    // Revenue Heatmap (values in displayCurrency)
    {
      const rows = [['Property', ...ML, 'YTD']];
      for (let pi = 0; pi < props.length; pi++) {
        const p = props[pi];
        const row = [p.name];
        for (let mi = 0; mi < 12; mi++) {
          row.push(p.monthly[mi]?.revenue ?? 0);
        }
        row.push(p.revenue_native);
        rows.push(row);
      }
      const ws = XLSX.utils.aoa_to_sheet(rows);
      // Column widths
      ws['!cols'] = rows[0].map((h, ci) => {
        let max = String(h || '').length;
        for (let ri = 1; ri < Math.min(rows.length, 40); ri++) {
          const cell = rows[ri]?.[ci];
          if (cell != null) max = Math.max(max, String(cell).length);
        }
        return { wch: Math.max(max + 2, 10) };
      });
      // Number formats
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let ci = 1; ci <= 13; ci++) {
        for (let ri = 1; ri <= range.e.r; ri++) {
          const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
          if (ws[addr] && typeof ws[addr].v === 'number') ws[addr].z = CUR_FMT;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, `Revenue after OTA Heatmap (${dc})`);
    }

    // Net Margin Heatmap (%)
    buildHeatmap(
      'Net Margin Heatmap',
      (p, mi) => (p.monthly[mi]?.margin_pct ?? 0) / 100,
      (p) => (p.margin_pct || 0) / 100,
      PCT_FMT,
    );

    // Occupancy Heatmap (%)
    buildHeatmap(
      'Occupancy Heatmap',
      (p, mi) => (p.monthly[mi]?.occupancy ?? 0) / 100,
      (p) => (p.occupancy_ytd_pct || 0) / 100,
      PCT_FMT,
    );

    // Net Nightly Rate Heatmap (values in displayCurrency)
    buildHeatmap(
      `Net Nightly Rate (${dc})`,
      (p, mi) => p.monthly[mi]?.avg_nightly_rate ?? 0,
      (p) => p.nights <= 0 ? 0 : p.avg_nightly_rate_native,
      CUR_FMT,
    );

  // ── Country tab: Properties ──
  } else if (countrySubTab === 'properties') {
    const cur = dc;
    const props = derived.propertiesYtd
      .filter(p => p.region === topTab)
      .sort((a, b) => b.net_revenue_native - a.net_revenue_native);

    // Sheet 1: YTD Summary
    const ytdRows = [['Property', `Revenue (${dc})`, `${COST_BUCKET_LABELS.ota_commission} (${dc})`, `${PNL_SUBTOTAL_LABELS.net_revenue} (${dc})`, `${COST_BUCKET_LABELS.management_fees} (${dc})`, `Cleaning (${dc})`, `Repairs (${dc})`, `Other (${dc})`, `${COST_BUCKET_LABELS.financing} (${dc})`, `${PNL_SUBTOTAL_LABELS.net_profit} (${dc})`, 'Margin %', 'Occupancy %', `Avg Net Nightly Rate (${dc})`, 'ROI %', 'Bookings Count']];
    for (const p of props) {
      ytdRows.push([
        p.name,
        p.gross_revenue_native,
        p.ota_commission_native > 0 ? -p.ota_commission_native : 0,
        p.net_revenue_native,
        p.management_fees_native,
        p.cleaning_native || 0,
        p.repairs_native || 0,
        p.other_operational_native || 0,
        p.financing_native || 0,
        p.net_margin_native,
        (p.margin_pct || 0) / 100,
        (p.occupancy_ytd_pct || 0) / 100,
        p.avg_nightly_rate_native,
        p.roi_pct != null ? p.roi_pct / 100 : '',
        p.bookings_count,
      ]);
    }
    addSheet('YTD Summary', ytdRows, {
      formats: { 1: CUR_FMT, 2: CUR_FMT, 3: CUR_FMT, 4: CUR_FMT, 5: CUR_FMT, 6: CUR_FMT, 7: CUR_FMT, 8: CUR_FMT, 9: CUR_FMT, 10: PCT_FMT, 11: PCT_FMT, 12: CUR_FMT, 13: PCT_FMT },
    });

    // Sheet 2: Monthly Drill-Down
    const drillRows = [['Property', 'Month', 'Revenue', COST_BUCKET_LABELS.ota_commission, PNL_SUBTOTAL_LABELS.net_revenue, COST_BUCKET_LABELS.management_fees, 'Cleaning', 'Repairs', 'Other', COST_BUCKET_LABELS.financing, PNL_SUBTOTAL_LABELS.net_profit, 'Occupancy %', 'Avg Net Nightly Rate', 'Bookings Count']];
    for (const p of props) {
      for (const m of p.monthly) {
        drillRows.push([
          p.name,
          m.label,
          m.gross_revenue,
          m.ota_commission > 0 ? -m.ota_commission : 0,
          m.net_revenue,
          m.management_fees,
          m.cleaning || 0,
          m.repairs || 0,
          m.other_operational || 0,
          m.financing || 0,
          m.net_margin,
          (m.occupancy || 0) / 100,
          m.avg_nightly_rate,
          m.bookings,
        ]);
      }
    }
    addSheet('Monthly Drill-Down', drillRows, {
      formats: { 2: CUR_FMT, 3: CUR_FMT, 4: CUR_FMT, 5: CUR_FMT, 6: CUR_FMT, 7: CUR_FMT, 8: CUR_FMT, 9: CUR_FMT, 10: CUR_FMT, 11: PCT_FMT, 12: CUR_FMT },
    });

  // ── Country tab: Bookings ──
  } else if (countrySubTab === 'bookings') {
    // Booking amounts are always in region's native currency
    const cur = topTab === 'uk' ? 'GBP' : 'EUR';
    let filtered = derived.bookings.filter(b => b.region === topTab);
    if (filters.properties.length > 0) {
      filtered = filtered.filter(b => filters.properties.includes(b.property_name));
    }
    if (filters.channels.length > 0) {
      filtered = filtered.filter(b => filters.channels.includes(b.channel || 'other'));
    }
    filtered = [...filtered].sort((a, b) => (b.check_in || '').localeCompare(a.check_in || ''));

    // Build filter description
    const filterParts = [];
    if (filters.properties.length > 0) filterParts.push(`Properties: ${filters.properties.join(', ')}`);
    if (filters.channels.length > 0) filterParts.push(`Channels: ${filters.channels.join(', ')}`);

    const bkHeader = ['Booking ID', 'Property', 'Channel', 'Guest Name', 'Check-in', 'Check-out', 'Nights', 'Revenue after OTA Native', 'Currency', `Revenue after OTA ${dc}`, 'Status', 'Source URL'];
    const bkRows = [];

    if (hasFilters) {
      bkRows.push([`Filtered by: ${filterParts.join(' / ')}`]);
    }
    bkRows.push(bkHeader);

    const toDate = (s) => {
      if (!s) return '';
      const d = new Date(s + 'T00:00:00');
      return isNaN(d.getTime()) ? s : d;
    };

    for (const b of filtered) {
      bkRows.push([
        b.booking_id || b._id || '',
        b.property_name,
        b.channel || '',
        b.guest_name || '',
        toDate(b.check_in),
        toDate(b.check_out),
        b.nights || 0,
        parseFloat(b.revenue) || 0,
        b.currency || cur,
        b.revenue_gbp || 0,
        b.status || '',
        b.source_url || b.listing_url || '',
      ]);
    }

    const merges = hasFilters ? [{ s: { r: 0, c: 0 }, e: { r: 0, c: bkHeader.length - 1 } }] : undefined;
    const dataStartRow = hasFilters ? 2 : 1;
    const DATE_FMT = 'yyyy-mm-dd';
    const bkFormats = { 4: DATE_FMT, 5: DATE_FMT, 7: CUR_FMT, 9: CUR_FMT };

    const ws = XLSX.utils.aoa_to_sheet(bkRows);

    // Auto-fit column widths
    const hdrRow = hasFilters ? bkRows[1] : bkRows[0];
    ws['!cols'] = hdrRow.map((h, ci) => {
      let max = String(h || '').length;
      const start = hasFilters ? 2 : 1;
      for (let ri = start; ri < Math.min(bkRows.length, 40); ri++) {
        const cell = bkRows[ri]?.[ci];
        if (cell != null) max = Math.max(max, String(cell).length);
      }
      return { wch: Math.max(max + 2, 10) };
    });

    if (merges) ws['!merges'] = merges;

    // Apply number formats
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let ci = range.s.c; ci <= range.e.c; ci++) {
      const fmt = bkFormats[ci];
      if (!fmt) continue;
      for (let ri = dataStartRow + 1; ri <= range.e.r; ri++) {
        const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (ws[addr] && typeof ws[addr].v === 'number') {
          ws[addr].z = fmt;
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Bookings');

    // Sheet 2: Monthly Subtotals
    const { monthlyGroups, totals } = bookingsData || { monthlyGroups: [], totals: { count: 0, nights: 0, gross: 0 } };
    const msRows = [['Month', 'Bookings Count', 'Nights', `Booking Volume (${cur})`]];
    for (const g of monthlyGroups) {
      if (g.count === 0) continue;
      msRows.push([g.label, g.count, g.nights, g.gross]);
    }
    msRows.push(['Grand Total', totals.count, totals.nights, totals.gross]);
    addSheet('Monthly Subtotals', msRows, { formats: { 3: CUR_FMT } });
  }

  XLSX.writeFile(wb, fileName);
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export function PropertyPortfolioPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [topTab, setTopTab] = useState('all');
  const [countrySubTab, setCountrySubTab] = useState('overview');
  const [dataset, setDataset] = useState(null);
  const [priorYearDataset, setPriorYearDataset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [missingCostsMap, setMissingCostsMap] = useState({});
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);

  // Bookings filter state (lifted for PDF export access)
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  // Forecast: fetch rolling 90-day forward bookings for the current year
  const { forecastByMonth } = useForecast({ scope: 'portfolio', region: 'all', year });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [data, priorData, missingCostsData] = await Promise.all([
        portfolioAPI.getDataset({ year }),
        portfolioAPI.getDataset({ year: year - 1 }).catch(() => null),
        propertyCostsAPI.getMissingCosts(year).catch(() => null),
      ]);
      setDataset(data);
      setPriorYearDataset(priorData);
      // Build property_id -> missing_count map for cost-completeness badges
      // getMissingCosts returns raw axios response; unwrap .data
      const mcMap = {};
      const mcReport = missingCostsData?.data?.report;
      if (mcReport) {
        for (const entry of mcReport) {
          mcMap[entry.property_id] = entry.missing_count;
        }
      }
      setMissingCostsMap(mcMap);
    } catch (err) {
      console.error('Portfolio dataset fetch failed:', err);
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset country sub-tab when switching away from a country tab
  useEffect(() => {
    if (topTab === 'all') {
      setCountrySubTab('overview');
    }
  }, [topTab]);

  const derivedBase = useMemo(() => computeDerivedMetrics(dataset), [dataset]);
  const derived = useMemo(
    () => convertDerivedToDisplayCurrency(derivedBase, displayCurrency),
    [derivedBase, displayCurrency]
  );

  // Prior-year derived metrics (reuses the same aggregation, year-shifted)
  const priorYearDerivedBase = useMemo(() => computeDerivedMetrics(priorYearDataset), [priorYearDataset]);
  const priorYearDerived = useMemo(
    () => convertDerivedToDisplayCurrency(priorYearDerivedBase, displayCurrency),
    [priorYearDerivedBase, displayCurrency]
  );
  const priorYearMonthly = priorYearDerived?.monthlyByRegion || null;

  // Filtered bookings data (for the active country tab's bookings view + export)
  const bookingsExportData = useMemo(() => {
    if (!derived || topTab === 'all') return null;
    const regionBookings = derived.bookings.filter(b => b.region === topTab);
    return buildBookingsTabMonthlyGroups(regionBookings, year, {
      properties: selectedProperties,
      channels: selectedChannels,
    });
  }, [derived, topTab, selectedProperties, selectedChannels, year]);

  // Reset bookings filters when switching country tabs
  useEffect(() => {
    setSelectedProperties([]);
    setSelectedChannels([]);
  }, [topTab]);

  const handleExportPDF = async () => {
    if (!derived) return;
    setIsGeneratingPDF(true);
    try {
      await exportPDF({
        derived,
        year,
        topTab,
        countrySubTab: topTab === 'all' ? null : countrySubTab,
        filters: { properties: selectedProperties, channels: selectedChannels },
        bookingsData: bookingsExportData,
        displayCurrency,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    if (!derived) return;
    setIsGeneratingExcel(true);
    try {
      await exportExcel({
        derived,
        year,
        topTab,
        countrySubTab: topTab === 'all' ? null : countrySubTab,
        filters: { properties: selectedProperties, channels: selectedChannels },
        bookingsData: bookingsExportData,
        displayCurrency,
      });
      toast.success('Excel downloaded');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // Property count subtitle
  const subtitle = useMemo(() => {
    if (!derived) return '';
    const ukCount = derived.propertiesYtd.filter(p => p.region === 'uk').length;
    const frCount = derived.propertiesYtd.filter(p => p.region === 'fr').length;
    const total = ukCount + frCount;
    return `${total} properties across \uD83C\uDDEC\uD83C\uDDE7 UK (${ukCount}) and \uD83C\uDDEB\uD83C\uDDF7 France (${frCount})`;
  }, [derived]);

  // ── Loading state ──
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <Home className="h-6 w-6" />Property Portfolio
              </h1>
            </div>
          </div>
          {/* Skeleton KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i}>
                <CardContent className="py-6">
                  <div className="h-4 w-20 bg-muted rounded animate-pulse mb-2" />
                  <div className="h-8 w-28 bg-muted rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Skeleton chart */}
          <Card>
            <CardContent className="py-6">
              <div className="h-[300px] bg-muted/50 rounded animate-pulse flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-6 p-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Home className="h-6 w-6" />Property Portfolio
          </h1>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to load portfolio data</h3>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchData}>Retry</Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // ── Empty state ──
  if (!derived || derived.bookings.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Home className="h-6 w-6" />Property Portfolio
            </h1>
            <YearNav year={year} setYear={setYear} />
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Home className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No bookings for {year}</h3>
              <p className="text-sm text-muted-foreground">Try a different year.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // ── Main render ──
  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Home className="h-6 w-6" />
              Property Portfolio
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          </div>

          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={isGeneratingExcel}
            >
              {isGeneratingExcel
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export Excel
            </Button>
            <YearNav year={year} setYear={setYear} />
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* July 2026 restatement notice retired (Fan, 2026-10-25): its
            904k figure no longer matches the live UK revenue. */}

        {/* Top Tabs -- prominent pill style */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
          {[
            { key: 'all', label: 'All' },
            { key: 'fr', label: '\uD83C\uDDEB\uD83C\uDDF7 France' },
            { key: 'uk', label: '\uD83C\uDDEC\uD83C\uDDE7 UK' },
          ].map(t => (
            <button
              key={t.key}
              className={cn(
                'px-5 py-2 text-sm font-semibold rounded-md transition-all',
                topTab === t.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setTopTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {topTab === 'all' ? (
          <AllTabContent derived={derived} nativeTotals={derivedBase?.totals} displayCurrency={displayCurrency} forecastByMonth={forecastByMonth} year={year} priorYearMonthly={priorYearMonthly} />
        ) : (
          <>
            {/* Country sub-tabs -- subordinate underline style */}
            <div className="flex gap-4 border-b">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'properties', label: 'Properties' },
                { key: 'bookings', label: 'Bookings' },
              ].map(t => (
                <button
                  key={t.key}
                  className={cn(
                    'px-1 pb-2 text-xs font-medium border-b-2 transition-colors',
                    countrySubTab === t.key
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                  onClick={() => setCountrySubTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {countrySubTab === 'overview' && <CountryOverviewContent region={topTab} derived={derived} displayCurrency={displayCurrency} priorYearMonthly={priorYearMonthly} year={year} forecastByMonth={forecastByMonth} />}
            {countrySubTab === 'properties' && <CountryPropertiesContent region={topTab} derived={derived} displayCurrency={displayCurrency} missingCostsMap={missingCostsMap} />}
            {countrySubTab === 'bookings' && <CountryBookingsContent region={topTab} derived={derived} displayCurrency={displayCurrency} year={year} selectedProperties={selectedProperties} setSelectedProperties={setSelectedProperties} selectedChannels={selectedChannels} setSelectedChannels={setSelectedChannels} monthlyGroups={bookingsExportData?.monthlyGroups || []} totals={bookingsExportData?.totals || { count: 0, nights: 0, gross: 0, otaCommission: 0, netRevenue: 0 }} />}
          </>
        )}

        {/* Data quality warning */}
        {dataset?.data_quality && (
          (dataset.data_quality.missing_primary_amount_count > 0 || dataset.data_quality.bookings_with_no_cost_data > 0) && (
            <div className="p-3 rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950 text-sm">
              <div className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Data quality:</span>
                {[
                  dataset.data_quality.missing_primary_amount_count > 0 && `${dataset.data_quality.missing_primary_amount_count} bookings excluded (missing amount)`,
                  dataset.data_quality.bookings_with_no_cost_data > 0 && `${dataset.data_quality.bookings_with_no_cost_data} bookings missing cost data`,
                ].filter(Boolean).join(', ')}
              </div>
            </div>
          )
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Year Navigator ──────────────────────────────────────────────────────────

function YearNav({ year, setYear }) {
  return (
    <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
      <button onClick={() => setYear(y => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="font-semibold w-12 text-center">{year}</span>
      <button
        onClick={() => setYear(y => y + 1)}
        disabled={year >= new Date().getFullYear()}
        className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
